import React, { useState, useEffect, useMemo } from 'react';
import TimeSelect, { BusinessHoursRange } from '../TimeSelect';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  MenuItem,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Tooltip,
} from '@mui/material';
import { Close, Launch, Folder, WarningAmber, EventRepeat } from '@mui/icons-material';
import { colors, typography, inputs } from '../../theme/designSystem';
import SavingOverlay from '../SavingOverlay';
import {
  SeriesSlotRow,
  WeekdayPicker,
  generateSeriesDates,
  buildSeriesRows,
  validateSeriesRows,
  seriesListSx,
  seriesLinkSx,
  toIsoDate,
  WEEKDAYS_PT,
} from '../schedule/seriesShared';
import { titleCaseName } from '../../utils/nameCase';
import { patientRegisterUrl, isOnPatientRegisterPage } from '../../utils/patientRegisterUrl';
import {
  ScheduleOptionItem,
  ScheduleProfessionalOption,
  SchedulePatientDiscount,
  ScheduleSeriesItem,
  ScheduleHistoryEntry,
  ScheduleSlot,
  RescheduleSeriesPayload,
  ScheduleItem,
} from '../../services/schedulesService';
import { ScheduleBlockItem } from '../../services/scheduleBlocksService';

// Data financeira exibida sem conversão de fuso: formata dd/MM/yyyy do próprio ISO.
const formatPaymentDate = (iso: string): string => {
  const parts = iso.slice(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : '';
};

// Interface para os dados do agendamento
export interface AppointmentData {
  patient: string;
  firstResponsible: string;
  secondResponsible: string;
  professional: string;
  team: string;
  serviceType: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  appointmentType: 'unique' | 'recurring';
  recurrenceType: string;
  maxOccurrences: number;
  unitValue: string;
  discount: string;
  totalUnit: string;
  totalValue: string;
  observations: string;
  confirmationStatus: string;
  attendanceStatus: string;
  /** motivo do cancelamento (reason_to_cancellation) — obrigatório ao cancelar */
  cancellationReason?: string;
  /** Ids resolvidos para a gravação (fase B): preenchidos no onSave quando em modo vinculado */
  patientId?: string | null;
  professionalId?: string | null;
  teamId?: string | null;
  serviceTypeId?: string | null;
  /** edição em série: true = mover a série toda pelo mesmo deslocamento */
  applyToSeries?: boolean;
  /** criação recorrente: datas removidas na prévia (exceções do gerador) */
  skippedDates?: string[];
  /** criação recorrente: sessões como a lista as mostra, com os ajustes à mão (RN-118m) */
  slots?: ScheduleSlot[];
  /** sala do atendimento (RN-548): id da sala; vazio = sala padrão do profissional */
  roomId?: string | null;
}

// Props do componente
interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Promise = gravação real: o modal mostra o overlay, fecha no sucesso e exibe o erro
   *  sem fechar na falha (a série no Bubble leva ~1s por ocorrência) */
  onSave: (data: AppointmentData) => void | Promise<void>;
  /** "Agendar em seguida" (item 145): chamado APOS gravar, com o que acabou de ser salvo, para o
   *  pai reabrir o modal encostado neste. Sem a prop, o botao nao aparece. */
  onScheduleNext?: (data: AppointmentData) => void;
  mode: 'create' | 'edit';
  initialData?: Partial<AppointmentData>;
  /** RN-409i: alterações pré-aplicadas POR CIMA do gravado (initialData) na abertura — o
   *  form nasce "sujo" (Salvar habilitado) e o dirty-check mostra a mudança como pendente.
   *  Sem isso, pré-preencher o horário da vaga direto no initialData deixava Salvar travado. */
  prefillOverrides?: Partial<AppointmentData>;
  title?: string;
  patientsList: string[];
  /** Opções reais (nomes) dos combos; ausentes = listas de exemplo (telas ainda em mock) */
  professionalsList?: string[];
  teamsList?: string[];
  serviceTypesList?: string[];
  /** pago: bloqueia a exclusão e mostra o aviso (com a data do pagamento) */
  paid?: boolean;
  /** data do pagamento (ISO); exibida no aviso quando pago */
  paymentDate?: string | null;
  /** valor efetivamente pago — alerta de diferença pago × valor vigente (RN-118d.1) */
  paymentFinalValue?: number | null;
  paymentDiscount?: number | null;
  /** perfil profissional (data_scope own): não troca profissional, equipe nem serviço —
   *  só data/horário, status e observações */
  ownProfileLock?: boolean;
  showPricing?: boolean; // Controla a visibilidade dos campos de valores
  disablePatientField?: boolean; // Desabilita o campo de paciente
  /** Vínculos reais (RN-115): presentes = equipe/serviço restritos ao profissional
   * selecionado (desabilitados sem profissional) e valores calculados por
   * professional_service_values + desconto do paciente */
  professionalOptions?: ScheduleProfessionalOption[];
  /** salas ativas (RN-548); sem lista o campo Sala não aparece */
  roomOptions?: ScheduleOptionItem[];
  teamOptions?: ScheduleOptionItem[];
  serviceTypeOptions?: ScheduleOptionItem[];
  /** Busca os descontos ativos do paciente pelo NOME exibido; resolve id no chamador */
  loadPatientDiscounts?: (patientName: string) => Promise<SchedulePatientDiscount[]>;
  /** Busca os responsáveis do paciente pelo NOME exibido (campos somente leitura) */
  loadPatientInfo?: (patientName: string) => Promise<
    { responsible1: string | null; responsible2: string | null } | null>;
  /** Busca server-side de pacientes (>=3 letras) — mesmo formato de rótulo da agenda */
  searchPatients?: (term: string) => Promise<string[]>;
  /** Feriados ativos na data (RN-407): retorna os nomes; presente = aviso não bloqueante */
  loadHolidaysOnDate?: (date: string) => Promise<string[]>;
  /** Catálogos reais de status (RN-114); ausentes = listas de exemplo (telas em mock) */
  confirmationOptions?: string[];
  presenceOptions?: string[];
  /** Horário de atendimento da clínica (RN-118o): recorta as listas de horário (TimeSelect) */
  businessHours?: BusinessHoursRange[] | null;
  /** Confirmações FINALIZADORAS (RN-114b): com uma delas escolhida, a presença é
      bloqueada e volta ao padrão — cancelado não tem presença. */
  terminalConfirmations?: string[];
  /** Ocorrências da série (edição): alimenta os diálogos de mover/excluir */
  seriesItems?: ScheduleSeriesItem[];
  /** Pacote do serviço da série (RN-228/RN-227): tamanho e modo de cobrança; nulo quando não é pacote */
  seriesPackage?: { size: number | null; chargeMode: 'total' | 'attended' | null } | null;
  /** Id da ocorrência aberta (edição) — para marcar "(este)" nas listas dos diálogos */
  currentScheduleId?: string | null;
  /** Exclusão lógica (fase B): presente = botão Excluir real com motivo obrigatório */
  onDelete?: (opts: { reason: string; includeFollowing: boolean }) => Promise<void>;
  /** Janela de edição vencida: modal inteiro em somente leitura (preserva o histórico) */
  locked?: boolean;
  /** Texto da faixa do modo leitura; sem ele vale o aviso da janela de edição */
  lockedMessage?: string;
  /** Perfil só com "confirmação e presença" (RN-115d): o resto do agendamento fica travado */
  attendanceOnly?: boolean;
  /** RN-115d: sem "excluir agendamento" o botão aparece desabilitado, com o motivo, e não abre a confirmação */
  canDelete?: boolean;
  /** RN-115d: sem "confirmação e presença" os status ficam travados, mesmo com "editar" */
  statusLocked?: boolean;
  /** Remarcações da ocorrência (num_reschedule) — exibido no histórico */
  rescheduleCount?: number;
  /** Histórico da trilha de auditoria; presente = seção Histórico na edição */
  loadHistory?: () => Promise<ScheduleHistoryEntry[]>;
  /** Calendário de feriados ativos da entidade (data ISO → nome) — sinaliza as prévias */
  loadHolidayCalendar?: () => Promise<Record<string, string>>;
  /** Navega o modal para outra ocorrência da série (recarrega os dados da clicada) */
  onOpenOccurrence?: (id: string, dateTimeInitial: string) => Promise<void> | void;
  /** Lista da série editável (RN-118m): grava remanejamentos e remoções e recarrega a série */
  onRescheduleSeries?: (payload: RescheduleSeriesPayload) => Promise<void>;
  /** Bloqueios do profissional no período (RN-118n): presente = o modal avisa quando o horário
   *  cai num bloqueio. Só avisa — o bloqueio é marcação e não impede agendar */
  loadBlocks?: (professionalId: string, from: string, to: string) => Promise<ScheduleBlockItem[]>;
  /** Agendamentos (não cancelados) do profissional e do paciente no período: presente = o modal
   *  avisa quando a data/horário coincide com outro compromisso. Só avisa, não impede */
  loadConflicts?: (args: { professionalId: string | null; patient: string; from: string; to: string }) => Promise<ScheduleItem[]>;
}

// Listas de exemplo usadas quando a tela chamadora ainda não passa as opções reais
const FALLBACK_PROFESSIONALS = [
  'Dr. João Silva', 'Dra. Maria Santos', 'Dr. Pedro Costa', 'Dra. Ana Oliveira',
  'Dr. Carlos Ferreira', 'Dra. Lucia Almeida', 'Dr. Roberto Lima', 'Dra. Patricia Rocha',
];
const FALLBACK_TEAMS = ['Equipe A', 'Equipe B', 'Equipe C'];
const FALLBACK_SERVICE_TYPES = ['Consulta', 'Exame', 'Procedimento', 'Retorno', 'Avaliação'];

// Garante que o valor atual (ex.: agendamento real carregado) esteja entre as opções,
// evitando o aviso "out-of-range value" do MUI e um select em branco na edição.
const withCurrent = (options: string[], current?: string): string[] =>
  current && !options.includes(current) ? [current, ...options] : options;


const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onScheduleNext,
  mode,
  initialData = {},
  prefillOverrides,
  title,
  patientsList,
  professionalsList,
  teamsList,
  serviceTypesList,
  paid = false,
  paymentDate = null,
  paymentFinalValue = null,
  paymentDiscount = null,
  ownProfileLock = false,
  showPricing = true,
  disablePatientField = false,
  professionalOptions,
  teamOptions,
  serviceTypeOptions,
  loadPatientDiscounts,
  loadPatientInfo,
  searchPatients,
  loadHolidaysOnDate,
  confirmationOptions,
  businessHours,
  presenceOptions,
  terminalConfirmations,
  seriesItems,
  seriesPackage = null,
  currentScheduleId,
  onDelete,
  locked = false,
  lockedMessage,
  attendanceOnly = false,
  canDelete = true,
  statusLocked = false,
  rescheduleCount = 0,
  loadHistory,
  loadHolidayCalendar,
  onOpenOccurrence,
  onRescheduleSeries,
  loadBlocks,
  loadConflicts,
  roomOptions
}) => {
  // Edição plena (menos paciente) dentro da janela de edição; locked = só leitura
  const fieldsLocked = locked || attendanceOnly;
  const isEdit = mode === 'edit';

  // Atalho para o cadastro do paciente (pedido do Hilton, 29/08): só com paciente identificado
  // e fora do próprio cadastro — abre em nova aba, mesmo padrão da fila de oportunidades
  const patientRegisterLink = initialData.patientId && !isOnPatientRegisterPage() ? (
    <Tooltip title="Abrir cadastro do paciente em nova aba">
      <IconButton
        component="a"
        href={patientRegisterUrl(initialData.patientId)}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        aria-label="Abrir cadastro do paciente em nova aba"
        sx={{
          width: inputs.default.height,
          height: inputs.default.height,
          flexShrink: 0,
          borderRadius: '6px',
          bgcolor: '#03a9f4',
          color: 'white',
          '&:hover': { bgcolor: '#0288d1' },
        }}
      >
        <Folder fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;
  // Estado do formulário
  const [formData, setFormData] = useState<AppointmentData>({
    patient: '',
    firstResponsible: '',
    secondResponsible: '',
    professional: '',
    team: '',
    serviceType: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    appointmentType: 'unique',
    recurrenceType: '',
    maxOccurrences: 1,
    unitValue: '',
    discount: '',
    totalUnit: '',
    totalValue: '',
    observations: '',
    confirmationStatus: '',
    attendanceStatus: '',
    ...initialData,
    ...(prefillOverrides ?? {})
  });

  // Atualizar form quando initialData mudar (overrides por cima — ver prefillOverrides).
  // O serviço é trabalhado por NOME; quando o chamador manda o id e o nome gravado não bate com
  // nenhuma opção (rótulo com plano na frente — item 177 — ou nome antigo), o id decide.
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const byId = initialData.serviceTypeId
        ? (serviceTypeOptions ?? []).find(s => s.id === initialData.serviceTypeId)
        : undefined;
      const knownName = !!initialData.serviceType
        && (serviceTypeOptions ?? []).some(s => s.name === initialData.serviceType);
      const serviceFix = byId && !knownName ? { serviceType: byId.name } : {};
      setFormData(prev => ({ ...prev, ...initialData, ...serviceFix, ...(prefillOverrides ?? {}) }));
    }
  }, [initialData, prefillOverrides, serviceTypeOptions]);

  // Troca de horário inicial PELO USUÁRIO preserva a duração atual (default 1h).
  // Não é useEffect de propósito: efeito disparava no carregamento da edição e
  // sobrescrevia o horário final gravado (sessões de 30min viravam 1h).
  const toMinutes = (t: string): number => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const fromMinutes = (total: number): string => {
    const capped = Math.min(Math.max(total, 0), 23 * 60 + 59);
    return `${String(Math.floor(capped / 60)).padStart(2, '0')}:${String(capped % 60).padStart(2, '0')}`;
  };
  // Depois que o usuario define o horario FINAL a mao, ele manda: mexer no inicial passa a
  // DESLOCAR a sessao mantendo a duracao que ELE escolheu, e nunca a duracao herdada.
  // Sem isso a duracao de origem (1h do default, ou a da sessao anterior no "agendar em seguida")
  // voltava a cada toque no horario inicial, e ficava impossivel montar uma sessao de 30 minutos
  // — achado do Hilton em 25/09/2026, testando a dobradinha.
  const [endTouched, setEndTouched] = useState(false);
  useEffect(() => { setEndTouched(false); }, [isOpen, initialData.startTime, initialData.endTime]);

  const handleStartTimeChange = (newStart: string) => {
    setFormData(prev => {
      if (!newStart) {
        return { ...prev, startTime: newStart };
      }
      // >>> REGRA (Hilton, 25/09/2026): o que o usuario digita MANDA. Duracao sugerida — a do
      //     default, a da sessao anterior no "agendar em seguida", ou a que o servico vier a
      //     trazer — e sugestao, nunca imposicao. Depois que ele define o horario final a mao,
      //     mexer no inicial NAO mexe mais no final; se o fim ficar menor que o inicio, o campo
      //     mostra o erro e ele corrige. Antes disso, deslocar o fim e conveniencia util.
      if (endTouched) {
        return { ...prev, startTime: newStart };
      }
      const duration = prev.startTime && prev.endTime && toMinutes(prev.endTime) > toMinutes(prev.startTime)
        ? toMinutes(prev.endTime) - toMinutes(prev.startTime)
        : 60;
      return { ...prev, startTime: newStart, endTime: fromMinutes(toMinutes(newStart) + duration) };
    });
  };

  const handleEndTimeChange = (newEnd: string) => {
    setEndTouched(true);
    setFormData(prev => ({ ...prev, endTime: newEnd }));
  };

  // Modo "vinculado" (RN-115): equipe/serviço restritos ao profissional e valores reais.
  // Sem professionalOptions (telas ainda em mock), mantém o comportamento anterior.
  const linkedMode = !!professionalOptions;
  const selectedProfessional = professionalOptions?.find(p => p.name === formData.professional) ?? null;
  // Sala (RN-548): ao trocar o profissional a sala padrão dele entra, a menos que o usuário já tenha escolhido outra à mão
  const [roomTouched, setRoomTouched] = useState(false);
  useEffect(() => {
    if (!roomOptions || roomTouched || locked) return;
    const def = selectedProfessional?.defaultRoomId ?? null;
    setFormData(prev => (prev.roomId === def ? prev : { ...prev, roomId: def }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfessional?.id]);
  useEffect(() => { setRoomTouched(!!initialData.roomId); }, [initialData.roomId, isOpen]);
  const selectedTeamOption = (teamOptions ?? []).find(t => t.name === formData.team) ?? null;
  const teamNamesForProfessional = linkedMode
    ? (teamOptions ?? [])
        .filter(t => (selectedProfessional?.teamIds ?? []).includes(t.id))
        .map(t => t.name)
    : (teamsList ?? FALLBACK_TEAMS);
  // Linhas de valor (PSV) do profissional compatíveis com a equipe selecionada —
  // o valor é por serviço E equipe; linha sem equipe vale para qualquer equipe.
  // (?? []: durante deploy a API pode responder o shape anterior sem os campos novos)
  const professionalServiceRows = (selectedProfessional?.services ?? []).filter(v =>
    !selectedTeamOption || !v.teamId || v.teamId === selectedTeamOption.id);

  // Linha de valor (PSV) do serviço escolhido. Resolve pelos IDS que o profissional tem, porque o
  // catálogo repete NOMES com ids distintos (duplicatas históricas) — casar por nome pega o id de
  // outra linha e devolve vazio. Preferência: equipe selecionada > linha sem equipe > única linha.
  // Fonte única do valor unitário e da duração prevista.
  const resolveServiceRow = () => {
    if (!selectedProfessional || !formData.serviceType) return undefined;
    const idsForName = new Set((serviceTypeOptions ?? [])
      .filter(s => s.name === formData.serviceType)
      .map(s => s.id));
    const rows = professionalServiceRows.filter(v => idsForName.has(v.serviceTypeId));
    if (rows.length === 0) return undefined;
    const teamRow = selectedTeamOption ? rows.find(v => v.teamId === selectedTeamOption.id) : undefined;
    return teamRow ?? rows.find(v => !v.teamId) ?? (rows.length === 1 ? rows[0] : undefined);
  };

  // Duracao do servico (professional_services_values.session_time). Serve so para AVISAR.
  const duracaoPrevista = resolveServiceRow()?.sessionMinutes ?? null;
  const duracaoMontada = formData.startTime && formData.endTime
    && toMinutes(formData.endTime) > toMinutes(formData.startTime)
    ? toMinutes(formData.endTime) - toMinutes(formData.startTime)
    : null;
  // Avisa, NAO bloqueia (decisao do Hilton, 25/09/2026): sessao que estende ou encurta e caso
  // real — dobradinha, encaixe, atendimento que rendeu menos. O Salvar segue habilitado.
  const duracaoDivergente = duracaoPrevista !== null && duracaoMontada !== null
    && duracaoPrevista !== duracaoMontada;

  // O catálogo tem nomes repetidos com ids distintos (duplicatas históricas) — as listas
  // e o valor SEMPRE resolvem pelos ids do próprio profissional, nunca por nome no catálogo
  const serviceNamesForProfessional = linkedMode
    ? Array.from(new Set((serviceTypeOptions ?? [])
        .filter(s => professionalServiceRows.some(v => v.serviceTypeId === s.id))
        .map(s => s.name)))
    : (serviceTypesList ?? FALLBACK_SERVICE_TYPES);
  const dependentFieldsDisabled = linkedMode && !selectedProfessional;

  const resolveUnitValue = (): number | null => resolveServiceRow()?.value ?? null;

  // Descontos ativos do paciente (patient_discounts) — recarrega quando o paciente muda
  const [patientDiscounts, setPatientDiscounts] = useState<SchedulePatientDiscount[]>([]);
  useEffect(() => {
    if (!loadPatientDiscounts || !formData.patient) {
      setPatientDiscounts([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      loadPatientDiscounts(formData.patient)
        .then(rows => { if (!cancelled) setPatientDiscounts(rows); })
        .catch(() => { if (!cancelled) setPatientDiscounts([]); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [formData.patient, loadPatientDiscounts]);

  // Menus dos selects: abrem abaixo do campo, alinhados à esquerda (padrão do sistema)
  const selectMenuProps = {
    variant: 'menu' as const,
    anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
    transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  };

  // Busca server-side de pacientes dentro do modal (>=3 letras, com debounce) —
  // igual à busca da página da agenda; sem searchPatients, usa a lista recebida
  const [patientSearchOptions, setPatientSearchOptions] = useState<string[]>([]);
  // O nome do paciente é chave de comparação exata na agenda: a caixa só muda na exibição, fora da digitação
  const [patientFocused, setPatientFocused] = useState(false);
  useEffect(() => {
    if (!searchPatients) {
      return;
    }
    const term = formData.patient.trim();
    if (term.length < 3) {
      setPatientSearchOptions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchPatients(term)
        .then(options => { if (!cancelled) setPatientSearchOptions(options); })
        .catch(() => { if (!cancelled) setPatientSearchOptions([]); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.patient, searchPatients]);

  // Responsáveis do paciente (somente leitura) — preenchidos ao selecionar o paciente
  useEffect(() => {
    if (!loadPatientInfo) {
      return;
    }
    if (!formData.patient) {
      setFormData(prev => ({ ...prev, firstResponsible: '', secondResponsible: '' }));
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      loadPatientInfo(formData.patient)
        .then(info => {
          if (cancelled) return;
          setFormData(prev => ({
            ...prev,
            firstResponsible: info?.responsible1 ?? '',
            secondResponsible: info?.responsible2 ?? ''
          }));
        })
        .catch(() => undefined);
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.patient, loadPatientInfo]);

  // Desconto vigente para a seleção atual (R$ por atendimento), espelho de ScheduleDiscounts.Pick da API.
  // Critério nulo na linha = não restringe; entre regras sobrepostas vale a de cadastro mais recente (nunca acumula).
  const resolveDiscount = (): number => {
    if (!selectedProfessional) return 0;
    // Resolve o id do serviço restrito aos serviços do profissional (nomes repetem no catálogo)
    const svcOption = (serviceTypeOptions ?? [])
      .filter(s => s.name === formData.serviceType)
      .find(s => (selectedProfessional.services ?? []).some(v => v.serviceTypeId === s.id));
    const teamOption = selectedTeamOption;
    const refDate = formData.startDate
      ? formData.startDate.slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    const candidates = patientDiscounts.filter(d => {
      if (d.initialDate && d.initialDate.slice(0, 10) > refDate) return false;
      if (d.finalDate && d.finalDate.slice(0, 10) < refDate) return false;
      if (d.serviceTypeId && d.serviceTypeId !== svcOption?.id) return false;
      if (d.professionalIds.length > 0 && !d.professionalIds.includes(selectedProfessional.id)) return false;
      if (d.teamId && teamOption && d.teamId !== teamOption.id) return false;
      if (d.professionalTypeId && selectedProfessional.professionalTypeId
        && d.professionalTypeId !== selectedProfessional.professionalTypeId) return false;
      return true;
    });
    if (candidates.length === 0) return 0;
    const best = [...candidates].sort((a, b) =>
      (b.createdAt ?? '').localeCompare(a.createdAt ?? '') || (b.initialDate ?? '').localeCompare(a.initialDate ?? ''))[0];
    return best.discount;
  };

  // Tabela de preços mock — usada apenas fora do modo vinculado (telas ainda em mock)
  const servicePrices: { [key: string]: { unitValue: number; discount: number } } = {
    'Consulta': { unitValue: 150.00, discount: 10.00 },
    'Exame': { unitValue: 200.00, discount: 15.00 },
    'Procedimento': { unitValue: 300.00, discount: 20.00 },
    'Retorno': { unitValue: 80.00, discount: 5.00 },
    'Avaliação': { unitValue: 120.00, discount: 8.00 }
  };

  // Calcular valores automaticamente. Na edição: enquanto profissional/equipe/serviço
  // estiverem como GRAVADOS, exibe os valores gravados do registro (recalcular mostraria
  // outro número); trocou algum vínculo, recalcula a prévia — o servidor refaz na gravação.
  // Pacote de sessões do serviço (RN-118j): pacote > 1 seleciona Recorrente e TRAVA a
  // quantidade no pacote (ex.: "Avaliação (4 sessões)" agenda as 4); pacote 1/nulo = livre.
  // Resolve pelos ids do profissional — o catálogo tem nomes duplicados.
  const selectedServicePackage = (() => {
    if (!linkedMode || !selectedProfessional || !formData.serviceType) return null;
    const option = (serviceTypeOptions ?? [])
      .filter(s => s.name === formData.serviceType)
      .find(s => (selectedProfessional.services ?? []).some(v => v.serviceTypeId === s.id));
    return option?.packageAmount ?? null;
  })();
  const packageLock = !isEdit && selectedServicePackage != null && selectedServicePackage > 1;

  // Tamanho da série que o campo Total representa (edição: o gravado; criação: a
  // recorrência escolhida) — alimenta o rótulo "Total da série (N sessões)"
  const totalSeriesSize = isEdit || formData.appointmentType === 'recurring'
    ? formData.maxOccurrences || 1
    : 1;

  // Marca quando foi o PACOTE que forçou a recorrência: ao voltar para serviço sem
  // pacote, desfaz só o que ele forçou (a quantidade ficava órfã — achado do Hilton, 21/08);
  // Recorrente escolhido manualmente pelo usuário não é tocado.
  const packageForced = React.useRef(false);
  // Ao trocar de ocorrência pela lista de recorrência, o conteúdo volta ao topo: o usuário
  // precisa VER que data/horário mudaram, e a lista fica no fim da rolagem.
  const contentRef = React.useRef<HTMLDivElement>(null);
  const openOccurrenceAndScrollTop = async (id: string, dateTimeInitial: string) => {
    await onOpenOccurrence?.(id, dateTimeInitial);
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  useEffect(() => {
    if (packageLock) {
      packageForced.current = true;
      if (formData.appointmentType !== 'recurring'
        || formData.maxOccurrences !== selectedServicePackage) {
        setFormData(prev => ({
          ...prev,
          appointmentType: 'recurring',
          maxOccurrences: selectedServicePackage!,
        }));
      }
      return;
    }
    if (packageForced.current) {
      packageForced.current = false;
      setFormData(prev => ({
        ...prev,
        appointmentType: 'unique',
        recurrenceType: '',
        maxOccurrences: 1,
      }));
    }
    // appointmentType/maxOccurrences nas deps: qualquer caminho que desvie do pacote
    // (ex.: alternar Único/Recorrente recalculava a quantidade) é corrigido na hora
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageLock, selectedServicePackage, formData.appointmentType, formData.maxOccurrences]);

  useEffect(() => {
    const refsMatchSaved = mode === 'edit'
      && (formData.professional || '') === (initialData.professional ?? '')
      && (formData.team || '') === (initialData.team ?? '')
      && (formData.serviceType || '') === (initialData.serviceType ?? '');
    if (refsMatchSaved) {
      setFormData(prev => ({
        ...prev,
        unitValue: initialData.unitValue ?? '',
        discount: initialData.discount ?? '',
        totalUnit: initialData.totalUnit ?? '',
        totalValue: initialData.totalValue ?? '',
      }));
      return;
    }
    const occurrences = formData.appointmentType === 'recurring' ? formData.maxOccurrences : 1;
    const apply = (unitValue: number, discount: number) => {
      const totalUnit = Math.max(unitValue - discount, 0);
      setFormData(prev => ({
        ...prev,
        unitValue: unitValue.toFixed(2),
        discount: discount.toFixed(2),
        totalUnit: totalUnit.toFixed(2),
        totalValue: (totalUnit * occurrences).toFixed(2)
      }));
    };
    const clear = () => setFormData(prev => ({
      ...prev, unitValue: '', discount: '', totalUnit: '', totalValue: ''
    }));

    if (linkedMode) {
      // Valor unitário por profissional + serviço + equipe (professional_service_values)
      const unit = resolveUnitValue();
      if (unit == null) {
        clear();
      } else {
        // Serviço com pacote (RN-118j): o PSV guarda o preço do PACOTE inteiro — a série
        // rateia por sessão (660 nas 4 = 165/sessão) e o Total volta ao valor do pacote
        const perSession = packageLock && selectedServicePackage
          ? Math.round((unit / selectedServicePackage) * 100) / 100
          : unit;
        apply(perSession, resolveDiscount());
      }
    } else if (formData.serviceType && servicePrices[formData.serviceType]) {
      const { unitValue, discount } = servicePrices[formData.serviceType];
      apply(unitValue, discount);
    } else {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.serviceType, formData.professional, formData.team, formData.startDate,
    formData.appointmentType, formData.maxOccurrences, patientDiscounts,
    packageLock, selectedServicePackage]);

  // Limpar formulário ao fechar (bloqueado durante a gravação da série)
  const handleClose = () => {
    if (saving) {
      return;
    }
    setSaveError(null);
    setMoveDialogOpen(false);
    setDeleteDialogOpen(false);
    setDeleteReason('');
    setMoveScope('single');
    setDeleteScope('single');
    setSkippedDates([]);
    setSlotEdits({});
    setWeekdays([]);
    previousStartDow.current = null;
    setFormData({
      patient: '',
      firstResponsible: '',
      secondResponsible: '',
      professional: '',
      team: '',
      serviceType: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      appointmentType: 'unique',
      recurrenceType: '',
      maxOccurrences: 1,
      unitValue: '',
      discount: '',
      totalUnit: '',
      totalValue: '',
      observations: '',
      confirmationStatus: '',
      attendanceStatus: '',
      cancellationReason: ''
    });
    onClose();
  };

  // Horário final precisa ser maior que o inicial (data única — mesmo dia)
  const endTimeInvalid = !!formData.startTime && !!formData.endTime
    && formData.endTime <= formData.startTime;

  // Recados da linha Data/Horários numa caixa só, atravessando as três colunas (pedido do Hilton,
  // 25/09/2026). Ancorados sob um campo eles quebravam em três linhas e vazavam; e o erro do
  // horário final fala da RELAÇÃO entre os dois campos, não de um deles. Cada linha nomeia o campo
  // a que se refere. Os dois recados não coexistem — sem fim maior que o início não há duração a
  // comparar —, então a caixa assume a cor do mais grave presente.
  const recadosDoHorario: { campo: string; texto: string }[] = [];
  if (endTimeInvalid) {
    recadosDoHorario.push({ campo: 'Horário final', texto: 'deve ser maior que o horário inicial.' });
  }
  if (duracaoDivergente) {
    recadosDoHorario.push({
      campo: 'Duração',
      texto: `${duracaoMontada} min, mas o serviço selecionado prevê ${duracaoPrevista} min. O agendamento pode ser gravado assim.`,
    });
  }

  // Aviso de feriado (RN-407): melhor esforço, nunca bloqueia nem quebra o agendamento.
  // Em recorrência, só a data inicial é verificada.
  const [holidayNames, setHolidayNames] = useState<string[]>([]);
  useEffect(() => {
    const date = formData.startDate ? formData.startDate.slice(0, 10) : '';
    if (!date || !loadHolidaysOnDate) {
      setHolidayNames([]);
      return;
    }
    let cancelled = false;
    loadHolidaysOnDate(date)
      .then(names => { if (!cancelled) setHolidayNames(names); })
      .catch(() => { if (!cancelled) setHolidayNames([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.startDate]);

  // Periodicidade da série na EDIÇÃO: não é persistida (nem no Bubble) — inferida pelos
  // intervalos entre as ocorrências; série mexida vira "Personalizada"
  const seriesPeriodicityLabel = useMemo(() => {
    // Só as ativas: excluída no meio da série distorceria os intervalos
    const items = (seriesItems ?? []).filter(i => i.active);
    if (items.length < 2) {
      return '';
    }
    const gaps: number[] = [];
    for (let i = 1; i < items.length; i++) {
      const prev = new Date(`${items[i - 1].dateTimeInitial.slice(0, 10)}T00:00:00`).getTime();
      const curr = new Date(`${items[i].dateTimeInitial.slice(0, 10)}T00:00:00`).getTime();
      gaps.push(Math.round((curr - prev) / 86400000));
    }
    const majority = (n: number) => gaps.filter(g => g === n).length >= Math.ceil(gaps.length / 2);
    if (majority(7)) return 'Semanal';
    if (majority(14)) return 'Quinzenal';
    // Mensal por posição no mês (RN-118i): gaps de 28 a 35 dias; cobre também as séries
    // antigas de passo fixo 28
    if (gaps.filter(g => g >= 28 && g <= 35).length >= Math.ceil(gaps.length / 2)) return 'Mensal';
    if (gaps.every(g => g >= 1 && g <= 4)) return 'Diária';
    return 'Personalizada';
  }, [seriesItems]);

  // Datas removidas da prévia (exceções): o gerador as pula e completa a série no fim
  const [skippedDates, setSkippedDates] = useState<string[]>([]);

  // Dias da semana da série (RN-118m), em Semanal/Quinzenal. O dia da data inicial é sempre
  // um deles e acompanha a data: trocar a data troca esse dia, os demais escolhidos ficam.
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const startDow = (() => {
    const d = formData.startDate ? new Date(`${formData.startDate.slice(0, 10)}T00:00:00`) : null;
    return d && !Number.isNaN(d.getTime()) ? d.getDay() : null;
  })();
  const previousStartDow = React.useRef<number | null>(null);
  useEffect(() => {
    if (startDow === null) {
      return;
    }
    setWeekdays(prev => {
      const kept = prev.filter(d => d !== previousStartDow.current && d !== startDow);
      return [...kept, startDow].sort((a, b) => a - b);
    });
    previousStartDow.current = startDow;
  }, [startDow, isOpen]);
  const toggleWeekday = (dow: number) => {
    if (dow === startDow) {
      return;
    }
    setWeekdays(prev => (prev.includes(dow) ? prev.filter(d => d !== dow) : [...prev, dow].sort((a, b) => a - b)));
  };

  // Ajustes à mão por linha (data e/ou horário), pela data que o gerador produziu
  const [slotEdits, setSlotEdits] = useState<Record<string, { date?: string; startTime?: string }>>({});

  // Exceção e ajuste pertencem à prévia que os gerou: trocar periodicidade, data inicial,
  // tipo ou dias da semana zera os dois — sem isso, X de um teste anterior seguia pulando
  // datas do novo modo e a série "esticava" para meses à frente. A quantidade zera só os
  // ajustes (as linhas se redistribuem).
  useEffect(() => {
    setSkippedDates([]);
    setSlotEdits({});
  }, [formData.recurrenceType, formData.startDate, formData.appointmentType, weekdays]);
  useEffect(() => {
    setSlotEdits({});
  }, [formData.maxOccurrences]);

  // Rótulos dos dois modos do Mensal (RN-118i), calculados da data inicial
  const monthlyAnchor = formData.startDate
    ? new Date(`${formData.startDate.slice(0, 10)}T00:00:00`)
    : null;
  const monthlyAnchorValid = monthlyAnchor !== null && !Number.isNaN(monthlyAnchor.getTime());
  const monthlyDayLabel = monthlyAnchorValid
    ? `Dia do mês (todo dia ${monthlyAnchor!.getDate()})`
    : 'Dia do mês';
  const monthlyWeekdayLabel = monthlyAnchorValid
    ? `Semana (${Math.floor((monthlyAnchor!.getDate() - 1) / 7) + 1}ª ${WEEKDAYS_PT[monthlyAnchor!.getDay()]} do mês)`
    : 'Posição da semana';

  const multiWeekday = (formData.recurrenceType === 'weekly' || formData.recurrenceType === 'biweekly')
    && weekdays.length > 1;

  // Prévia das datas da recorrência (criação): diário pula fim de semana; semanal +7;
  // quinzenal +14; mensal = mesma POSIÇÃO do dia da semana no mês-calendário (RN-118i — mês
  // sem a 5ª usa a última); exceções puladas. Com mais de um dia da semana (RN-118m), a
  // série percorre os dias escolhidos de cada semana (quinzenal: semana sim, semana não) e a
  // quantidade é o total de sessões. A lista final vai pronta ao servidor (slots).
  const recurrenceDates = useMemo(() => {
    if (isEdit || formData.appointmentType !== 'recurring'
      || !formData.startDate || !formData.recurrenceType) {
      return [] as Date[];
    }
    const count = Math.min(Math.max(formData.maxOccurrences || 0, 2), 52);
    const first = new Date(`${formData.startDate.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(first.getTime())) {
      return [] as Date[];
    }
    return generateSeriesDates({
      first,
      recurrenceType: formData.recurrenceType,
      count,
      skipped: skippedDates,
      weekdays: multiWeekday ? weekdays : [],
    });
  }, [isEdit, formData.appointmentType, formData.startDate,
    formData.recurrenceType, formData.maxOccurrences, skippedDates, multiWeekday, weekdays]);

  // Linhas da série como vão para o servidor (RN-118m): a data gerada ou a ajustada à mão, o
  // horário do topo ou o ajustado; o fim preserva a duração do topo. A 1ª linha é o topo.
  const topDuration = formData.startTime && formData.endTime
    && toMinutes(formData.endTime) > toMinutes(formData.startTime)
    ? toMinutes(formData.endTime) - toMinutes(formData.startTime)
    : 60;
  const creationRows = buildSeriesRows(recurrenceDates, slotEdits, formData.startTime, topDuration);

  // Conflitos entre linhas. A ordem da lista é estável de propósito: reordenar enquanto a
  // data é digitada tiraria a linha de baixo do cursor. O servidor ordena ao gravar.
  const startIso = formData.startDate ? formData.startDate.slice(0, 10) : '';
  const creationRowErrors = validateSeriesRows(creationRows, startIso, formData.startTime, topDuration, 'sessão');
  const creationSlotsInvalid = !isEdit && formData.appointmentType === 'recurring'
    && Object.keys(creationRowErrors).length > 0;
  const adjustedCount = creationRows.filter(r => r.adjusted).length;
  const editSlot = (key: string, patch: { date?: string; startTime?: string }) =>
    setSlotEdits(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // Feriados ativos da entidade (data ISO → nome) para sinalizar as prévias
  const [holidayMap, setHolidayMap] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!isOpen || !loadHolidayCalendar) {
      setHolidayMap({});
      return;
    }
    let cancelled = false;
    loadHolidayCalendar()
      .then(map => { if (!cancelled) setHolidayMap(map); })
      .catch(() => { if (!cancelled) setHolidayMap({}); });
    return () => { cancelled = true; };
    // lambda nova a cada render do chamador — depender dela criaria loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Histórico da trilha (edição): carregado ao abrir; null = carregando
  const [history, setHistory] = useState<ScheduleHistoryEntry[] | null>(null);
  useEffect(() => {
    if (!isOpen || !isEdit || !loadHistory) {
      setHistory(null);
      return;
    }
    let cancelled = false;
    loadHistory()
      .then(items => { if (!cancelled) setHistory(items); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
    // loadHistory é lambda nova a cada render do chamador — depender dela criaria loop.
    // initialData nas deps: a edição salva sem fechar e o pai rebaseia os dados — o
    // histórico recarrega junto para mostrar a alteração recém-gravada
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, currentScheduleId, initialData]);

  const formatHistoryDate = (iso: string): string =>
    `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} ${iso.slice(11, 16)}`;

  // Gravação real (fase B): overlay enquanto a série é escrita, erro exibido sem fechar
  const [saving, setSaving] = useState(false);
  const [savingMessage, setSavingMessage] = useState('Processando...');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Diálogos da edição: mover série e excluir com motivo
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveScope, setMoveScope] = useState<'single' | 'series'>('single');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteScope, setDeleteScope] = useState<'single' | 'following'>('single');
  const [deleteReason, setDeleteReason] = useState('');

  const currentSeriesItem = (seriesItems ?? []).find(i => i.id === currentScheduleId) ?? null;
  // "Este em diante" para os diálogos: só ATIVAS a partir da aberta (exclusive) —
  // excluídas aparecem na lista informativa, mas não voltam a ser afetadas
  const followingItems = currentSeriesItem
    ? (seriesItems ?? []).filter(i =>
        i.active && i.id !== currentSeriesItem.id
        && i.dateTimeInitial >= currentSeriesItem.dateTimeInitial)
    : [];

  // Série de PACOTE (item 191): não perde sessão. Com alguma sessão marcada ou paga, excluir
  // fica bloqueado (remaneja as que faltam); sem nenhuma, só sai o pacote INTEIRO.
  const activeSeriesItems = (seriesItems ?? []).filter(i => i.active);
  const packageSeries = isEdit && (selectedServicePackage ?? 0) > 1 && activeSeriesItems.length > 1;
  const packageStarted = packageSeries && activeSeriesItems.some(i => i.presenceMarked);
  const packageDeleteBlocked = packageSeries && activeSeriesItems.some(i => !i.pristine || i.paid);

  // Lista da série editável (RN-118m): rascunho próprio, aplicado de uma vez pelo botão da
  // lista — separado do Salvar do modal. A sessão aberta muda pelo topo, não pela lista.
  const [seriesEditing, setSeriesEditing] = useState(false);
  const [seriesDraft, setSeriesDraft] = useState<Record<string, { date?: string; startTime?: string }>>({});
  const [seriesRemoved, setSeriesRemoved] = useState<string[]>([]);
  const [seriesReason, setSeriesReason] = useState('');
  useEffect(() => {
    setSeriesEditing(false);
    setSeriesDraft({});
    setSeriesRemoved([]);
    setSeriesReason('');
  }, [seriesItems, currentScheduleId, isOpen]);

  const seriesLockReason = (item: ScheduleSeriesItem): string | undefined => {
    if (item.id === currentScheduleId) return 'Altere esta sessão pelos campos de data e horário acima.';
    if (item.paid) return 'Sessão paga: abra a sessão para alterá-la.';
    if (!item.pristine || item.cancelled) return 'Sessão com status marcado: abra a sessão para alterá-la.';
    if (item.locked) return 'Fora da janela de edição.';
    return undefined;
  };
  const seriesRows = (seriesItems ?? []).filter(i => i.active).map(item => {
    const originalDate = item.dateTimeInitial.slice(0, 10);
    const originalStart = item.dateTimeInitial.slice(11, 16);
    const originalEnd = item.dateTimeFinal.slice(11, 16);
    // Legado com duração zero mantém zero: fim = início
    const duration = Math.max(toMinutes(originalEnd) - toMinutes(originalStart), 0);
    const draft = seriesDraft[item.id];
    const date = draft?.date ?? originalDate;
    const startTime = draft?.startTime ?? originalStart;
    return {
      item,
      date,
      startTime,
      endTime: startTime ? fromMinutes(toMinutes(startTime) + duration) : '',
      duration,
      editable: seriesLockReason(item) === undefined,
      changed: date !== originalDate || startTime !== originalStart,
      removed: seriesRemoved.includes(item.id),
    };
  });
  const seriesRowErrors: Record<string, string> = {};
  seriesRows.forEach(row => {
    if (row.removed || !row.editable) return;
    if (!row.date || !row.startTime) {
      seriesRowErrors[row.item.id] = 'Informe a data e o horário.';
    } else if (toMinutes(row.startTime) + row.duration > 24 * 60 - 1) {
      seriesRowErrors[row.item.id] = 'Passa da meia-noite.';
    } else {
      const clash = seriesRows.find(other => other !== row && !other.removed && other.date === row.date
        && (other.startTime === row.startTime
          || (other.startTime < row.endTime && row.startTime < other.endTime)));
      if (clash) {
        seriesRowErrors[row.item.id] = `Conflita com a sessão de ${clash.startTime}.`;
      }
    }
  });
  const seriesEditableCount = seriesRows.filter(r => r.editable).length;
  const seriesChanges = seriesRows.filter(r => r.editable && r.changed && !r.removed);
  const seriesPending = seriesChanges.length + seriesRemoved.length;
  const seriesReasonMissing = seriesRemoved.length > 0 && !seriesReason.trim();
  const seriesInvalid = Object.keys(seriesRowErrors).length > 0;

  // Bloqueios do profissional nas datas em jogo (topo, série nova e série em edição). A API
  // aceita janelas de até 62 dias: séries longas são consultadas em fatias de 60.
  const [professionalBlocks, setProfessionalBlocks] = useState<ScheduleBlockItem[]>([]);
  const blockDates = [
    formData.startDate ? formData.startDate.slice(0, 10) : '',
    ...creationRows.map(r => r.date),
    // Edição de série: todas as datas ativas, para a lista já abrir indicando conflito e bloqueio
    ...(isEdit ? seriesRows.map(r => r.date) : []),
  ].filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d)).sort();
  const blockProfessionalId = selectedProfessional?.id ?? '';
  const blockRangeKey = blockDates.length > 0 && blockProfessionalId
    ? `${blockProfessionalId}|${blockDates[0]}|${blockDates[blockDates.length - 1]}`
    : '';
  useEffect(() => {
    if (!isOpen || !loadBlocks || !blockRangeKey) {
      setProfessionalBlocks([]);
      return;
    }
    const [professionalId, from, to] = blockRangeKey.split('|');
    const windows: [string, string][] = [];
    let cursor = new Date(`${from}T00:00:00`);
    const last = new Date(`${to}T00:00:00`);
    while (cursor <= last) {
      const end = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 59);
      windows.push([toIsoDate(cursor), toIsoDate(end < last ? end : last)]);
      cursor = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
    }
    let cancelled = false;
    Promise.all(windows.map(([f, t]) => loadBlocks(professionalId, f, t)))
      .then(parts => { if (!cancelled) setProfessionalBlocks(parts.flat()); })
      .catch(() => { if (!cancelled) setProfessionalBlocks([]); });
    return () => { cancelled = true; };
    // lambda nova a cada render do chamador — a chave do período decide a busca
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, blockRangeKey]);

  // Bloqueios que cruzam [início, fim) no dia; texto pronto para o aviso
  const blockedAt = (date: string, start: string, end: string): string | undefined => {
    if (!date || !start) return undefined;
    const finish = end && end > start ? end : start;
    const hits = professionalBlocks.filter(b => b.dateTimeInitial.slice(0, 10) === date
      && b.dateTimeInitial.slice(11, 16) < (finish === start ? `${start}~` : finish)
      && b.dateTimeFinal.slice(11, 16) > start);
    // Dois bloqueios iguais no mesmo horário (duplicata) viram um texto só
    return hits.length > 0
      ? Array.from(new Set(hits.map(b => `${b.title} (${b.dateTimeInitial.slice(11, 16)}–${b.dateTimeFinal.slice(11, 16)})`))).join(', ')
      : undefined;
  };
  const topBlocked = blockedAt(formData.startDate ? formData.startDate.slice(0, 10) : '', formData.startTime, formData.endTime);

  // Outros compromissos do profissional e do paciente nas mesmas datas (pedido do Hilton, 30/09):
  // aviso por sessão, como o de bloqueio. Na edição, a sessão aberta e a série dela ficam de fora
  // — entre elas vale a checagem de sobreposição da própria lista.
  const [conflictItems, setConflictItems] = useState<ScheduleItem[]>([]);
  const conflictPatient = (formData.patient || '').trim();
  const conflictKey = blockDates.length > 0 && (blockProfessionalId || conflictPatient)
    ? `${blockProfessionalId}|${conflictPatient}|${blockDates[0]}|${blockDates[blockDates.length - 1]}`
    : '';
  useEffect(() => {
    if (!isOpen || !loadConflicts || !conflictKey) {
      setConflictItems([]);
      return;
    }
    const [professionalId, patient, from, to] = conflictKey.split('|');
    let cancelled = false;
    loadConflicts({ professionalId: professionalId || null, patient, from, to })
      .then(items => { if (!cancelled) setConflictItems(items); })
      .catch(() => { if (!cancelled) setConflictItems([]); });
    return () => { cancelled = true; };
    // lambda nova a cada render do chamador — a chave decide a busca
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, conflictKey]);

  const ownScheduleIds = new Set<string>(
    [currentScheduleId ?? '', ...(seriesItems ?? []).map(i => i.id)].filter(Boolean));
  const conflictAt = (date: string, start: string, end: string): string | undefined => {
    if (!date || !start) return undefined;
    const finish = end && end > start ? end : `${start}~`;
    const labels = conflictItems
      .filter(i => !ownScheduleIds.has(i.id)
        && i.dateTimeInitial.slice(0, 10) === date
        && i.dateTimeInitial.slice(11, 16) < finish
        && (i.dateTimeFinal > i.dateTimeInitial ? i.dateTimeFinal : i.dateTimeInitial).slice(11, 16) > start)
      .map(i => {
        const hours = `${i.dateTimeInitial.slice(11, 16)}–${i.dateTimeFinal.slice(11, 16)}`;
        const sameProfessional = !!blockProfessionalId && i.professionalId === blockProfessionalId;
        const samePatient = !!conflictPatient && (i.patientName ?? '').trim() === conflictPatient;
        if (sameProfessional && samePatient) return `já existe sessão deste paciente com este profissional (${hours})`;
        if (sameProfessional) return `profissional já atende ${i.patientName || 'outro paciente'} (${hours})`;
        return `paciente já tem sessão com ${i.professionalName || 'outro profissional'} (${hours})`;
      });
    return labels.length > 0 ? Array.from(new Set(labels)).join('; ') : undefined;
  };
  // Avisos de cada sessão da série (lista da edição, antes mesmo de "Editar datas")
  const seriesItemAlerts = (item: ScheduleSeriesItem): string[] => {
    const day = item.dateTimeInitial.slice(0, 10);
    const start = item.dateTimeInitial.slice(11, 16);
    const end = item.dateTimeFinal.slice(11, 16);
    const conflict = conflictAt(day, start, end);
    const blocked = blockedAt(day, start, end);
    return [conflict && `Conflito: ${conflict}`, blocked && `Horário bloqueado: ${blocked}`].filter(Boolean) as string[];
  };
  const seriesAlertCount = (seriesItems ?? [])
    .filter(i => i.active && !i.cancelled && seriesItemAlerts(i).length > 0).length;
  const topConflict = conflictAt(formData.startDate ? formData.startDate.slice(0, 10) : '', formData.startTime, formData.endTime);

  const cancelSeriesEditing = () => {
    setSeriesEditing(false);
    setSeriesDraft({});
    setSeriesRemoved([]);
    setSeriesReason('');
  };

  const applySeriesEditing = async () => {
    if (!onRescheduleSeries || seriesPending === 0 || seriesInvalid || seriesReasonMissing || saving) {
      return;
    }
    try {
      setSaveError(null);
      setSavingMessage('Gravando as datas da série...');
      setSaving(true);
      await onRescheduleSeries({
        moves: seriesChanges.map(r => ({
          scheduleId: r.item.id, date: r.date, startTime: r.startTime, endTime: r.endTime,
        })),
        deletions: seriesRemoved,
        deleteReason: seriesRemoved.length > 0 ? seriesReason.trim() : null,
      });
    } catch (error: any) {
      setSaveError(error?.message || 'Não foi possível gravar as datas da série. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // Exclusão bloqueada pelo GRAVADO (initialData), não pelo combo em edição
  const storedConfirmation = initialData.confirmationStatus ?? '';
  const storedPresence = initialData.attendanceStatus ?? '';
  const deleteBlockedByStatus = isEdit && !(
    (storedConfirmation === '' || storedConfirmation === 'Pendente confirmação')
    && (storedPresence === '' || storedPresence === 'Não informado'));

  const dateTimeChanged = isEdit && (
    (formData.startDate || '') !== (initialData.startDate ?? '')
    || (formData.startTime || '') !== (initialData.startTime ?? '')
    || (formData.endTime || '') !== (initialData.endTime ?? ''));

  // Vínculos alterados (profissional/equipe/serviço): também disparam a pergunta de série
  const refsChanged = isEdit && (
    (formData.professional || '') !== (initialData.professional ?? '')
    || (formData.team || '') !== (initialData.team ?? '')
    || (formData.serviceType || '') !== (initialData.serviceType ?? ''));

  // Dirty-check: em edição, Salvar só habilita com alguma mudança (qualquer campo)
  const isDirty = !isEdit || dateTimeChanged || refsChanged
    || (formData.confirmationStatus || '') !== (initialData.confirmationStatus ?? '')
    || (formData.attendanceStatus || '') !== (initialData.attendanceStatus ?? '')
    || (formData.observations || '') !== (initialData.observations ?? '')
    || (formData.cancellationReason || '') !== (initialData.cancellationReason ?? '');

  // Profissional, equipe e serviço são obrigatórios (decisão do Hilton, 10/08)
  const requiredMissing = !formData.professional || !formData.team || !formData.serviceType;

  // Cancelamento em curso: confirmação selecionada é "Cancelado *" — pede o motivo
  const isCancelling = (formData.confirmationStatus || '').startsWith('Cancelado');
  const cancellationReasonMissing = isEdit && isCancelling
    && (formData.confirmationStatus !== (initialData.confirmationStatus ?? ''))
    && !(formData.cancellationReason || '').trim();

  // Presença só a partir do DIA do atendimento (o servidor também valida)
  const presenceTooEarly = (() => {
    if (!isEdit || !formData.startDate) {
      return false;
    }
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return formData.startDate.slice(0, 10) > todayStr;
  })();

  // Diferença pago × valor vigente (RN-118d.1): alerta o usuário — o tratamento
  // (estorno/complemento) é etapa futura. Compara com a PRÉVIA em edição, descontando
  // o abatimento concedido NO pagamento: pagar 165 sobre valor 220 com desconto de 55
  // é quitação legítima, não diferença a cobrar (refinamento do Hilton, 21/08).
  const paymentDiff = (() => {
    if (!isEdit || !paid || paymentFinalValue == null || !formData.totalUnit) {
      return null;
    }
    const current = Number(formData.totalUnit);
    if (!Number.isFinite(current)) {
      return null;
    }
    const diff = current - (paymentDiscount ?? 0) - paymentFinalValue;
    return Math.abs(diff) > 0.009 ? diff : null;
  })();

  // A série ASSUME a nova data/horário da edição: cada ocorrência desloca os mesmos dias
  // e passa ao novo horário (prévia do diálogo de mover)
  const moveDayDelta = (() => {
    if (!initialData.startDate || !formData.startDate) {
      return 0;
    }
    const oldMs = new Date(`${initialData.startDate.slice(0, 10)}T00:00:00`).getTime();
    const newMs = new Date(`${formData.startDate.slice(0, 10)}T00:00:00`).getTime();
    return Number.isFinite(oldMs) && Number.isFinite(newMs)
      ? Math.round((newMs - oldMs) / 86400000)
      : 0;
  })();

  const formatSlot = (iso: string): string => {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
  };

  const endHourOf = (iso: string): string => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const formatMovedSlot = (iso: string): string => {
    const d = new Date(new Date(iso).getTime() + moveDayDelta * 86400000);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()} ${formData.startTime}`;
  };

  const buildPayload = (): AppointmentData => {
    const serviceOption = linkedMode && selectedProfessional
      ? (serviceTypeOptions ?? [])
          .filter(s => s.name === formData.serviceType)
          .find(s => (selectedProfessional.services ?? []).some(v => v.serviceTypeId === s.id))
      : undefined;
    // Edição sem troca de serviço mantém o id gravado: a linha de valor do profissional pode ter
    // sido inativada depois do agendamento, e o valor já está no próprio agendamento
    const savedService = initialData.serviceTypeId
      ? (serviceTypeOptions ?? []).find(s => s.id === initialData.serviceTypeId)
      : undefined;
    const initialServiceName = savedService
      && !(serviceTypeOptions ?? []).some(s => s.name === initialData.serviceType)
      ? savedService.name
      : initialData.serviceType;
    const keptServiceTypeId = isEdit && !serviceOption && initialData.serviceTypeId
      && formData.serviceType === initialServiceName
      && formData.professional === initialData.professional
      ? initialData.serviceTypeId
      : null;
    return {
      ...formData,
      // Data única: o fim é sempre no mesmo dia do início
      endDate: formData.startDate,
      professionalId: selectedProfessional?.id ?? null,
      teamId: selectedTeamOption?.id ?? null,
      serviceTypeId: serviceOption?.id ?? keptServiceTypeId,
      skippedDates,
      slots: !isEdit && formData.appointmentType === 'recurring'
        ? creationRows.map(r => ({ date: r.date, startTime: r.startTime, endTime: r.endTime }))
        : undefined,
    };
  };

  const performSave = async (payload: AppointmentData, seguir = false) => {
    try {
      setSaveError(null);
      setSavingMessage(payload.appointmentType === 'recurring' && !isEdit
        ? `Gravando ${payload.maxOccurrences} agendamentos...`
        : 'Gravando agendamento...');
      setSaving(true);
      await Promise.resolve(onSave(payload));
      // Edição fica aberta após salvar (o pai rebaseia initialData com o gravado);
      // criação fecha como antes
      if (!isEdit) {
        handleClose();
        // "Agendar em seguida": o pai reabre o modal encostado neste. Depois do handleClose para
        // o estado interno do modal ser zerado antes de receber os dados novos.
        if (seguir && onScheduleNext) {
          onScheduleNext(payload);
        }
      }
    } catch (error: any) {
      setSaveError(error?.message || 'Não foi possível gravar o agendamento. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // Salvar — em modo vinculado, anexa os IDS resolvidos (profissional, equipe, serviço);
  // o paciente é resolvido pelo chamador (nome -> id). Em edição de série com mudança de
  // data/horário, o diálogo pergunta se move a série toda (decisão do Hilton, 10/08)
  const handleSave = async (seguir = false) => {
    if (endTimeInvalid || saving || locked || !isDirty) {
      return;
    }
    // Série com ocorrências à frente + mudança que se propaga (data/horário ou vínculos):
    // pergunta se aplica a este e os seguintes
    if (isEdit && followingItems.length > 0 && (dateTimeChanged || refsChanged)) {
      setMoveScope('single');
      setMoveDialogOpen(true);
      return;
    }
    await performSave({ ...buildPayload(), applyToSeries: false }, seguir);
  };

  const confirmMove = async () => {
    setMoveDialogOpen(false);
    await performSave({ ...buildPayload(), applyToSeries: moveScope === 'series' });
  };

  const confirmDelete = async () => {
    if (!onDelete || deleteReason.trim().length === 0) {
      return;
    }
    setDeleteDialogOpen(false);
    try {
      setSaveError(null);
      setSavingMessage('Excluindo...');
      setSaving(true);
      await onDelete({
        reason: deleteReason.trim(),
        includeFollowing: packageSeries || deleteScope === 'following',
      });
      handleClose();
    } catch (error: any) {
      setSaveError(error?.message || 'Não foi possível excluir o agendamento. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          // 1080px: a lista de sessões (data + horário + fim por linha) precisa de ~460px na
          // coluna direita; no celular o Dialog segue ocupando a largura da tela
          maxWidth: '1080px',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          maxHeight: '90vh',
          position: 'relative',
          overflow: 'hidden',
          // Campo desabilitado uniforme: CSS global pinta só o input interno de cinza e
          // deixava uma lasca branca na moldura — o fundo passa a ser do root inteiro
          '& .MuiOutlinedInput-root.Mui-disabled': {
            backgroundColor: colors.disabledBackground,
          },
          '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input': {
            backgroundColor: 'transparent',
          },
        }
      }}
    >
      <SavingOverlay open={saving} message={savingMessage} />
      {/* Cabeçalho do modal */}
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: colors.white,
          padding: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontSize: '1.4rem',
            fontWeight: typography.fontWeight.semibold,
            margin: 0
          }}
        >
          {title || (mode === 'create' ? 'Novo Agendamento' : 'Editar Agendamento')}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={saving}
          sx={{
            color: colors.white,
            padding: '0.25rem',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {/* RN-115d: o que este perfil não altera no agendamento */}
      {!locked && !attendanceOnly && statusLocked && (
        <Box sx={{
          backgroundColor: '#E8F4F6',
          color: '#0b5e6a',
          padding: '0.6rem 1.5rem',
          fontSize: '0.85rem',
          fontWeight: typography.fontWeight.medium,
        }}>
          Seu perfil não altera a confirmação e a presença deste agendamento.
        </Box>
      )}
      {!locked && attendanceOnly && (
        <Box sx={{
          backgroundColor: '#E8F4F6',
          color: '#0b5e6a',
          padding: '0.6rem 1.5rem',
          fontSize: '0.85rem',
          fontWeight: typography.fontWeight.medium,
        }}>
          Seu perfil altera só a confirmação e a presença deste agendamento.
        </Box>
      )}

      {/* Janela de edição vencida: modal inteiro em modo leitura */}
      {locked && (
        <Box sx={{
          backgroundColor: '#FFF8E1',
          color: '#795548',
          padding: '0.6rem 1.5rem',
          fontSize: '0.85rem',
          fontWeight: typography.fontWeight.medium,
        }}>
          {lockedMessage ?? 'Somente leitura — fora da janela de edição (o histórico deste agendamento está preservado)'}
        </Box>
      )}

      {/* Erro da gravação: exibido sem fechar o modal (os dados preenchidos permanecem) */}
      {saveError && (
        <Box sx={{
          backgroundColor: '#FDECEA',
          color: '#B71C1C',
          padding: '0.6rem 1.5rem',
          fontSize: '0.85rem',
          fontWeight: typography.fontWeight.medium,
        }}>
          {saveError}
        </Box>
      )}

      {/* Conteúdo do modal */}
      <DialogContent ref={contentRef} sx={{ padding: '1.5rem !important', paddingTop: '2rem !important' }}>
        {/* Primeira linha: Paciente */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 0 }}
            freeSolo
            disabled={disablePatientField || isEdit || locked}
            options={searchPatients ? patientSearchOptions : patientsList}
            value={formData.patient}
            inputValue={patientFocused ? formData.patient : titleCaseName(formData.patient)}
            renderOption={(props, option) => <li {...props} key={option}>{titleCaseName(option)}</li>}
            onChange={(_, newValue) => {
              const patientName = newValue ? newValue.split(' - ')[0] : '';
              setFormData({ ...formData, patient: patientName });
            }}
            onInputChange={(_, newInputValue) => {
              setFormData({ ...formData, patient: newInputValue });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Paciente"
                placeholder="Digite pelo menos 3 letras"
                onFocus={() => setPatientFocused(true)}
                onBlur={() => setPatientFocused(false)}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: inputs.default.labelFontSize,
                    color: inputs.default.labelColor,
                    backgroundColor: inputs.default.labelBackground,
                    padding: inputs.default.labelPadding,
                    '&.Mui-focused': {
                      color: inputs.default.focus.labelColor,
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: inputs.default.height,
                    fontSize: inputs.default.fontSize,
                    '& fieldset': {
                      borderColor: inputs.default.borderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: inputs.default.hover.borderColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: inputs.default.focus.borderColor,
                      boxShadow: inputs.default.focus.boxShadow,
                    },
                  },
                }}
              />
            )}
          />
          {patientRegisterLink}
        </Box>

        {/* Segunda linha: Layout com 2 colunas */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '1.5rem' }}>
          {/* Coluna esquerda */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <TextField
              label="1º Responsável"
              value={titleCaseName(formData.firstResponsible)}
              placeholder="Não informado"
              disabled
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                  backgroundColor: colors.backgroundAlt,
                  '& fieldset': {
                    borderColor: inputs.default.borderColor,
                  },
                },
              }}
            />

            <TextField
              label="2º Responsável"
              value={titleCaseName(formData.secondResponsible)}
              placeholder="Não informado"
              disabled
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                  backgroundColor: colors.backgroundAlt,
                  '& fieldset': {
                    borderColor: inputs.default.borderColor,
                  },
                },
              }}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '1rem', alignItems: 'start' }}>
            {/* Profissional antes da Equipe (RN-116): equipe/serviço dependem dele */}
            <TextField
              select
              label="Profissional"
              value={formData.professional}
              disabled={fieldsLocked || ownProfileLock}
              error={!fieldsLocked && !ownProfileLock && !formData.professional}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => {
                  const next = { ...prev, professional: value };
                  if (linkedMode) {
                    // Equipe/serviço que não pertencem ao novo profissional são limpos
                    const p = professionalOptions!.find(x => x.name === value);
                    const teamOk = !!p && (teamOptions ?? [])
                      .some(t => t.name === prev.team && (p.teamIds ?? []).includes(t.id));
                    const svcOk = !!p && (serviceTypeOptions ?? [])
                      .some(s => s.name === prev.serviceType
                        && (p.services ?? []).some(v => v.serviceTypeId === s.id));
                    if (!teamOk) next.team = '';
                    if (!svcOk) next.serviceType = '';
                  }
                  return next;
                });
              }}
              SelectProps={{ MenuProps: selectMenuProps }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.select.labelFontSize,
                  color: inputs.select.labelColor,
                  backgroundColor: inputs.select.labelBackground,
                  padding: inputs.select.labelPadding,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: inputs.select.height,
                  fontSize: inputs.select.fontSize,
                  '& fieldset': {
                    borderColor: inputs.select.borderColor,
                  },
                },
              }}
            >
              {/* Campo obrigatório: placeholder só aparece enquanto vazio (não dá para esvaziar) */}
              {!formData.professional && (
                <MenuItem value="" disabled>Selecione um profissional</MenuItem>
              )}
              {withCurrent(professionalsList ?? FALLBACK_PROFESSIONALS, formData.professional).map(name => (
                <MenuItem key={name} value={name}>{titleCaseName(name)}</MenuItem>
              ))}
            </TextField>

            {roomOptions && (
              <TextField
                select
                label="Sala"
                value={formData.roomId ?? ''}
                disabled={fieldsLocked || !formData.professional}
                onChange={(e) => { setRoomTouched(true); setFormData(prev => ({ ...prev, roomId: e.target.value || null })); }}
                helperText={!formData.professional
                  ? 'Selecione o profissional primeiro'
                  : selectedProfessional?.defaultRoomName
                    ? `Padrão do profissional: ${selectedProfessional.defaultRoomName}`
                    : 'Profissional sem sala padrão'}
                SelectProps={{ MenuProps: selectMenuProps, displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                FormHelperTextProps={{ sx: { mx: 0 } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: inputs.default.height,
                    fontSize: inputs.default.fontSize,
                    '& fieldset': { borderColor: inputs.default.borderColor },
                  },
                }}
              >
                <MenuItem value="">Sem sala definida</MenuItem>
                {roomOptions.map(r => (
                  <MenuItem key={r.id} value={r.id}>{r.name}{r.id === selectedProfessional?.defaultRoomId ? ' (padrão)' : ''}</MenuItem>
                ))}
              </TextField>
            )}
            </Box>

            {/* Equipe e Serviço em linhas separadas (pedido do Hilton, 25/09/2026): o rótulo do
                serviço de convênio leva o plano na frente e não cabe em meia largura. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', alignItems: 'start' }}>

            <TextField
              select
              label="Equipe"
              value={formData.team}
              onChange={(e) => {
                const value = e.target.value;
                setFormData(prev => {
                  const next = { ...prev, team: value };
                  if (linkedMode && selectedProfessional && prev.serviceType) {
                    // Serviço incompatível com a nova equipe é limpo (valor é por serviço+equipe)
                    const newTeam = (teamOptions ?? []).find(t => t.name === value) ?? null;
                    const rows = (selectedProfessional.services ?? []).filter(v =>
                      !newTeam || !v.teamId || v.teamId === newTeam.id);
                    const idsForName = new Set((serviceTypeOptions ?? [])
                      .filter(s => s.name === prev.serviceType)
                      .map(s => s.id));
                    if (!rows.some(v => idsForName.has(v.serviceTypeId))) {
                      next.serviceType = '';
                    }
                  }
                  return next;
                });
              }}
              disabled={dependentFieldsDisabled || fieldsLocked || (ownProfileLock && isEdit)}
              error={!dependentFieldsDisabled && !fieldsLocked && !(ownProfileLock && isEdit) && !formData.team}
              helperText={dependentFieldsDisabled && !fieldsLocked ? 'Selecione o profissional primeiro' : undefined}
              SelectProps={{ MenuProps: selectMenuProps }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.select.labelFontSize,
                  color: inputs.select.labelColor,
                  backgroundColor: inputs.select.labelBackground,
                  padding: inputs.select.labelPadding,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: inputs.select.height,
                  fontSize: inputs.select.fontSize,
                  '& fieldset': {
                    borderColor: inputs.select.borderColor,
                  },
                },
              }}
            >
              {!formData.team && (
                <MenuItem value="" disabled>Selecione uma equipe</MenuItem>
              )}
              {withCurrent(teamNamesForProfessional, formData.team).map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Tipo de serviço"
              value={formData.serviceType}
              onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
              disabled={dependentFieldsDisabled || fieldsLocked || (ownProfileLock && isEdit)}
              error={!dependentFieldsDisabled && !fieldsLocked && !(ownProfileLock && isEdit) && !formData.serviceType}
              helperText={dependentFieldsDisabled && !fieldsLocked ? 'Selecione o profissional primeiro' : undefined}
              SelectProps={{ MenuProps: selectMenuProps }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.select.labelFontSize,
                  color: inputs.select.labelColor,
                  backgroundColor: inputs.select.labelBackground,
                  padding: inputs.select.labelPadding,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: inputs.select.height,
                  fontSize: inputs.select.fontSize,
                  '& fieldset': {
                    borderColor: inputs.select.borderColor,
                  },
                },
              }}
            >
              {!formData.serviceType && (
                <MenuItem value="" disabled>Tipo de serviço</MenuItem>
              )}
              {withCurrent(serviceNamesForProfessional, formData.serviceType).map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </TextField>
            </Box>

            <TextField
              label="Observações"
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              disabled={fieldsLocked}
              placeholder="Observações"
              multiline
              rows={2}
              InputLabelProps={{
                shrink: inputs.multiline.labelShrink,
                sx: {
                  fontSize: inputs.multiline.labelFontSize,
                  color: inputs.multiline.labelColor,
                  backgroundColor: inputs.multiline.labelBackground,
                  padding: inputs.multiline.labelPadding,
                  '&.Mui-focused': {
                    color: colors.primary,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  position: inputs.multiline.position,
                  opacity: inputs.multiline.opacity,
                  alignItems: inputs.multiline.alignItems,
                  fontSize: inputs.multiline.fontSize,
                  minHeight: inputs.multiline.minHeight,
                  maxHeight: inputs.multiline.maxHeight,
                  overflow: inputs.multiline.overflow,
                  padding: 0,
                  '& fieldset': {
                    borderColor: inputs.multiline.borderColor,
                  },
                  '&:hover fieldset': {
                    borderColor: inputs.multiline.borderColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary,
                  },
                  '& textarea': {
                    wordWrap: inputs.multiline.wordWrap,
                    whiteSpace: inputs.multiline.whiteSpace,
                    padding: inputs.multiline.inputPadding,
                    height: inputs.multiline.textareaHeight,
                    maxHeight: inputs.multiline.textareaMaxHeight,
                    overflow: `${inputs.multiline.textareaOverflow} !important`,
                    boxSizing: inputs.multiline.textareaBoxSizing,
                    '&::-webkit-scrollbar': {
                      width: inputs.multiline.scrollbarWidth,
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: inputs.multiline.scrollbarTrackColor,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: inputs.multiline.scrollbarThumbColor,
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: inputs.multiline.scrollbarThumbHoverColor,
                      },
                    },
                  },
                },
              }}
            />
          </Box>

          {/* Coluna direita */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Data única (RN-116): o agendamento é do dia — a mesma data vale para início
                e fim; só os horários variam. Atendimento não cruza meia-noite. */}
            {/* Data ganha a maior fatia: "dd/mm/aaaa" + ícone não cabem em 1fr (achado do
                Hilton, 21/08); os horários (hh:mm) precisam de menos */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: '1rem' }}>
              <TextField
                label="Data"
                type="date"
                value={formData.startDate}
                disabled={fieldsLocked}
                error={!fieldsLocked && !formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value, endDate: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: inputs.default.labelFontSize,
                    color: inputs.default.labelColor,
                    backgroundColor: inputs.default.labelBackground,
                    padding: inputs.default.labelPadding,
                    '&.Mui-focused': {
                      color: inputs.default.focus.labelColor,
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: inputs.default.height,
                    fontSize: inputs.default.fontSize,
                    '& fieldset': {
                      borderColor: inputs.default.borderColor,
                      legend: {
                        maxWidth: inputs.default.legendMaxWidth,
                      },
                    },
                    '&:hover fieldset': {
                      borderColor: inputs.default.hover.borderColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: inputs.default.focus.borderColor,
                      boxShadow: inputs.default.focus.boxShadow,
                    },
                  },
                }}
              />

              {/* Lista de 5 em 5 minutos no lugar do relógio nativo (decisão do Hilton, 01/10/2026) */}
              <TimeSelect
                label="Horário inicial"
                value={formData.startTime}
                businessHours={businessHours}
                disabled={fieldsLocked}
                error={!fieldsLocked && !formData.startTime}
                onChange={handleStartTimeChange}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: inputs.default.labelFontSize,
                    color: inputs.default.labelColor,
                    backgroundColor: inputs.default.labelBackground,
                    padding: inputs.default.labelPadding,
                    '&.Mui-focused': {
                      color: inputs.default.focus.labelColor,
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: inputs.default.height,
                    fontSize: inputs.default.fontSize,
                    '& fieldset': {
                      borderColor: inputs.default.borderColor,
                      legend: {
                        maxWidth: inputs.default.legendMaxWidth,
                      },
                    },
                    '&:hover fieldset': {
                      borderColor: inputs.default.hover.borderColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: inputs.default.focus.borderColor,
                      boxShadow: inputs.default.focus.boxShadow,
                    },
                  },
                }}
              />

              <TimeSelect
                label="Horário final"
                value={formData.endTime}
                after={formData.startTime || undefined}
                businessHours={businessHours}
                disabled={fieldsLocked}
                onChange={handleEndTimeChange}
                error={endTimeInvalid || (!fieldsLocked && !formData.endTime)}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: inputs.default.labelFontSize,
                    color: inputs.default.labelColor,
                    backgroundColor: inputs.default.labelBackground,
                    padding: inputs.default.labelPadding,
                    '&.Mui-focused': {
                      color: inputs.default.focus.labelColor,
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: inputs.default.height,
                    fontSize: inputs.default.fontSize,
                    '& fieldset': {
                      borderColor: inputs.default.borderColor,
                      legend: {
                        maxWidth: inputs.default.legendMaxWidth,
                      },
                    },
                    '&:hover fieldset': {
                      borderColor: inputs.default.hover.borderColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: inputs.default.focus.borderColor,
                      boxShadow: inputs.default.focus.boxShadow,
                    },
                  },
                }}
              />

              {/* gridColumn 1/-1: a caixa atravessa as tres colunas. Sem isso ela vira a quarta
                  celula da grade e cai espremida embaixo da Data. Mesmo desenho da caixa de
                  feriado, logo abaixo. Erro pinta de vermelho; so aviso, de amarelo — e o
                  agendamento com duracao divergente continua gravavel. */}
              {recadosDoHorario.length > 0 && (
                <Box
                  sx={{
                    gridColumn: '1 / -1',
                    backgroundColor: endTimeInvalid ? colors.errorSoft : colors.warningSoft,
                    border: `1px solid ${endTimeInvalid ? '#f5c2c7' : '#ffe69c'}`,
                    color: endTimeInvalid ? '#842029' : '#664d03',
                    borderRadius: '8px',
                    padding: '0.6rem 0.9rem',
                    fontSize: '0.85rem',
                    marginTop: '-0.25rem',
                  }}
                >
                  {recadosDoHorario.map(r => (
                    <Box key={r.campo} sx={{ '& + &': { marginTop: '0.35rem' } }}>
                      <strong>{r.campo}:</strong> {r.texto}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {topConflict && (
              <Box
                role="status"
                sx={{
                  backgroundColor: colors.warningSoft,
                  border: '1px solid #ffe69c',
                  color: '#664d03',
                  borderRadius: '8px',
                  padding: '0.6rem 0.9rem',
                  fontSize: '0.85rem',
                  marginTop: '-0.75rem',
                }}
              >
                <strong>Conflito de horário:</strong> {topConflict}. O agendamento pode ser gravado assim.
              </Box>
            )}

            {topBlocked && (
              <Box
                role="status"
                sx={{
                  backgroundColor: colors.warningSoft,
                  border: '1px solid #ffe69c',
                  color: '#664d03',
                  borderRadius: '8px',
                  padding: '0.6rem 0.9rem',
                  fontSize: '0.85rem',
                  marginTop: '-0.75rem',
                }}
              >
                <strong>Horário bloqueado</strong> na agenda de {formData.professional || 'o profissional'}: {topBlocked}.
                O agendamento pode ser gravado assim.
              </Box>
            )}

            {holidayNames.length > 0 && (
              <Box
                sx={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffe69c',
                  color: '#664d03',
                  borderRadius: '8px',
                  padding: '0.6rem 0.9rem',
                  fontSize: '0.85rem',
                  marginTop: '-0.75rem',
                }}
              >
                Feriado na data selecionada: {holidayNames.join(', ')}. O agendamento não é
                bloqueado — confirme se a clínica atende neste dia.
              </Box>
            )}

            {/* Tipo de agendamento */}
            <FormControl>
              <FormLabel
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: typography.fontWeight.semibold,
                  color: '#2D3748',
                  marginBottom: '0.5rem',
                  '&.Mui-focused': {
                    color: '#2D3748',
                  },
                }}
              >
                Tipo de agendamento
              </FormLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <RadioGroup
                  row
                  value={formData.appointmentType}
                  onChange={(e) => setFormData({
                    ...formData,
                    appointmentType: e.target.value as 'unique' | 'recurring',
                    recurrenceType: e.target.value === 'unique' ? '' : formData.recurrenceType,
                    maxOccurrences: e.target.value === 'unique' ? 1 : Math.max(formData.maxOccurrences, 2)
                  })}
                  sx={{ flexShrink: 0 }}
                >
                  <FormControlLabel
                    value="unique"
                    disabled={isEdit || packageLock}
                    title={packageLock
                      ? `Serviço com pacote de ${selectedServicePackage} sessões — agendamento sempre recorrente`
                      : undefined}
                    control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                    label="Único"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                  />
                  <FormControlLabel
                    value="recurring"
                    disabled={isEdit}
                    control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                    label="Recorrente"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                  />
                </RadioGroup>
                {formData.appointmentType === 'recurring' && (
                  <TextField
                    type="number"
                    placeholder="Qtde máxima"
                    disabled={isEdit || packageLock}
                    title={packageLock ? `Pacote do serviço: ${selectedServicePackage} sessões` : undefined}
                    value={formData.maxOccurrences}
                    onChange={(e) => {
                      // Teto 52 (um ano semanal): a série inteira é gravada na criação e o
                      // Bubble impõe ~700ms por escrita — 52 já leva ~40s
                      const value = Number(e.target.value);
                      setFormData({ ...formData, maxOccurrences: value > 52 ? 52 : value });
                    }}
                    inputProps={{ min: 2, max: 52 }}
                    sx={{
                      width: '120px',
                      '& .MuiOutlinedInput-root': {
                        height: inputs.default.height,
                        fontSize: inputs.default.fontSize,
                        '& fieldset': {
                          borderColor: inputs.default.borderColor,
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </FormControl>

            {/* Periodicidade em coluna única com a prévia de datas AO LADO (pedido do
                Hilton, 11/08) — não persiste; na edição a série já existe, oculta */}
            {formData.appointmentType === 'recurring' && !isEdit && (
              // Datas ABAIXO da periodicidade, em largura cheia (como na edição) — ao lado,
              // as anotações de feriado quebravam em várias linhas
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <FormControl>
                  <FormLabel
                    sx={{
                      fontSize: '0.9rem',
                      fontWeight: typography.fontWeight.medium,
                      color: colors.textPrimary,
                      marginBottom: '0.25rem',
                      '&.Mui-focused': {
                        color: colors.textPrimary,
                      },
                    }}
                  >
                    Periodicidade
                  </FormLabel>
                  <RadioGroup
                    value={formData.recurrenceType.startsWith('monthly') ? 'monthly' : formData.recurrenceType}
                    onChange={(e) => setFormData({ ...formData, recurrenceType: e.target.value })}
                    sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}
                  >
                    <FormControlLabel
                      value="daily"
                      control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                      label="Diário"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                    />
                    <FormControlLabel
                      value="weekly"
                      control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                      label="Semanal"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                    />
                    <FormControlLabel
                      value="biweekly"
                      control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                      label="Quinzenal"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                    />
                    <FormControlLabel
                      value="monthly"
                      control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                      label="Mensal"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                    />
                    {/* Mensal em dois modos (RN-118i): pelo DIA do mês (default — mensalidades)
                        ou pela POSIÇÃO da semana (sessão clínica de dia fixo). Dentro do grid,
                        na coluna do Mensal — alinhado sob a opção que detalha */}
                    {formData.recurrenceType.startsWith('monthly') && (
                      <RadioGroup
                        value={formData.recurrenceType}
                        onChange={(e) => setFormData({ ...formData, recurrenceType: e.target.value })}
                        sx={{ gridColumn: { xs: '1', sm: '2' } }}
                      >
                        <FormControlLabel
                          value="monthly"
                          control={<Radio size="small" sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                          label={monthlyDayLabel}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                        <FormControlLabel
                          value="monthlyweekday"
                          control={<Radio size="small" sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                          label={monthlyWeekdayLabel}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                        />
                      </RadioGroup>
                    )}
                  </RadioGroup>
                </FormControl>

                {/* Dias da semana (RN-118m): só onde a semana é a unidade — Semanal e Quinzenal.
                    O dia da data inicial fica marcado e acompanha a data. */}
                {(formData.recurrenceType === 'weekly' || formData.recurrenceType === 'biweekly') && startDow !== null && (
                  <Box>
                    <WeekdayPicker value={weekdays} anchor={startDow} onToggle={toggleWeekday} />
                    {multiWeekday && (
                      <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary, marginTop: '0.4rem' }}>
                        {`${formData.maxOccurrences} sessões no total, ${formData.recurrenceType === 'biweekly' ? 'semana sim, semana não' : 'toda semana'}.`}
                      </Typography>
                    )}
                  </Box>
                )}

                {creationRows.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: '0.75rem', marginBottom: '0.25rem' }}>
                      <Typography sx={{
                        fontSize: '0.9rem',
                        fontWeight: typography.fontWeight.medium,
                        color: colors.textPrimary,
                      }}>
                        {`Sessões (${creationRows.length})`}
                      </Typography>
                      {skippedDates.length > 0 && (
                        <Button variant="text" size="small" onClick={() => setSkippedDates([])} sx={seriesLinkSx}>
                          {`restaurar removidas (${skippedDates.length})`}
                        </Button>
                      )}
                      {adjustedCount > 0 && (
                        <Button variant="text" size="small" onClick={() => setSlotEdits({})} sx={seriesLinkSx}>
                          {`desfazer ajustes (${adjustedCount})`}
                        </Button>
                      )}
                    </Box>
                    <Box sx={seriesListSx}>
                      {creationRows.map(row => (
                        <SeriesSlotRow
                          businessHours={businessHours}
                          key={row.key}
                          number={row.index + 1}
                          date={row.date}
                          startTime={row.startTime}
                          endTime={row.endTime}
                          minDate={startIso || undefined}
                          editable={row.index > 0}
                          lockedReason={row.index === 0 ? 'Primeira sessão: segue a data e o horário acima.' : undefined}
                          adjusted={row.adjusted}
                          holiday={holidayMap[row.date]}
                          blocked={blockedAt(row.date, row.startTime, row.endTime)}
                          conflict={conflictAt(row.date, row.startTime, row.endTime)}
                          error={creationRowErrors[row.key]}
                          onDateChange={(value) => editSlot(row.key, { date: value })}
                          onTimeChange={(value) => editSlot(row.key, { startTime: value })}
                          // Pacote não perde sessão (item 191); a 1ª é o topo. Remover pula a data
                          // gerada e a série completa no fim.
                          onRemove={row.index > 0 && !packageLock
                            ? () => {
                              setSlotEdits(prev => {
                                const next = { ...prev };
                                delete next[row.key];
                                return next;
                              });
                              setSkippedDates(prev => [...prev, row.key]);
                            }
                            : undefined}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Série (edição): linha-resumo com a periodicidade inferida (não é persistida
                — lida dos intervalos) + lista de datas em largura cheia */}
            {isEdit && (seriesItems ?? []).length > 1 && (
              <Box>
                {/* RN-228: a sessão aberta é parte de um pacote — faixa com posição e o retrato da série */}
                {seriesPackage?.size ? (() => {
                  const items = (seriesItems ?? []).filter(i => i.active);
                  const current = items.find(i => i.id === currentScheduleId);
                  const realizadas = items.filter(i => i.attended).length;
                  const faltas = items.filter(i => i.noShow).length;
                  const canceladas = items.filter(i => i.cancelled).length;
                  const aRealizar = items.length - realizadas - faltas - canceladas;
                  const partes = [
                    `${realizadas} realizada${realizadas === 1 ? '' : 's'}`,
                    faltas > 0 ? `${faltas} falta${faltas === 1 ? '' : 's'}` : null,
                    canceladas > 0 ? `${canceladas} cancelada${canceladas === 1 ? '' : 's'}` : null,
                    `${aRealizar} a realizar`,
                  ].filter(Boolean).join(', ');
                  return (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem',
                      padding: '0.5rem 0.75rem', marginBottom: '0.5rem', borderRadius: '6px',
                      backgroundColor: '#e0f5f8', borderLeft: '4px solid #0b7285', color: '#0b7285',
                    }}>
                      <EventRepeat sx={{ fontSize: '1rem' }} />
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: typography.fontWeight.semibold }}>
                        {`Pacote: sessão ${current?.occurrenceNumber ?? '?'} de ${seriesPackage.size}`}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem' }}>
                        {`· ${partes} · ${seriesPackage.chargeMode === 'attended' ? 'cobra só as realizadas' : 'cobra o pacote inteiro'}`}
                      </Typography>
                    </Box>
                  );
                })() : null}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  <Typography sx={{
                    fontSize: '0.9rem',
                    fontWeight: typography.fontWeight.medium,
                    color: colors.textPrimary,
                  }}>
                    {`Recorrência ${seriesPeriodicityLabel.toLowerCase()} — ${(seriesItems ?? []).filter(i => i.active).length} ativas de ${(seriesItems ?? []).length}`}
                    {seriesAlertCount > 0 && (
                      <Box component="span" sx={{ color: '#8a5300', fontWeight: typography.fontWeight.medium }}>
                        {` · ${seriesAlertCount} com aviso`}
                      </Box>
                    )}
                  </Typography>
                  {/* Lista editável (RN-118m): só com o agendamento aberto sem alteração pendente,
                      para os dois salvamentos não se misturarem */}
                  {onRescheduleSeries && !fieldsLocked && !seriesEditing && seriesEditableCount > 0 && (
                    <Tooltip
                      title={isDirty ? 'Salve ou cancele as alterações deste agendamento antes' : ''}
                      placement="top"
                      enterTouchDelay={300}
                    >
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={saving || isDirty}
                          onClick={() => setSeriesEditing(true)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            height: 30,
                            borderColor: colors.primary,
                            color: colors.primary,
                            '&:hover': { borderColor: colors.primaryHover, backgroundColor: colors.primaryLight },
                          }}
                        >
                          Editar datas
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                {seriesEditing ? (
                  <Box>
                    <Box sx={seriesListSx}>
                      {seriesRows.map(row => (
                        <SeriesSlotRow
                          businessHours={businessHours}
                          key={row.item.id}
                          number={row.item.occurrenceNumber ?? 0}
                          date={row.date}
                          startTime={row.startTime}
                          endTime={row.endTime}
                          editable={row.editable}
                          lockedReason={seriesLockReason(row.item)}
                          current={row.item.id === currentScheduleId}
                          adjusted={row.changed}
                          removed={row.removed}
                          holiday={holidayMap[row.date]}
                          blocked={row.removed ? undefined : blockedAt(row.date, row.startTime, row.endTime)}
                          conflict={row.removed ? undefined : conflictAt(row.date, row.startTime, row.endTime)}
                          error={seriesRowErrors[row.item.id]}
                          onDateChange={(value) => setSeriesDraft(prev => ({ ...prev, [row.item.id]: { ...prev[row.item.id], date: value } }))}
                          onTimeChange={(value) => setSeriesDraft(prev => ({ ...prev, [row.item.id]: { ...prev[row.item.id], startTime: value } }))}
                          // Pacote não perde sessão pela lista (item 191): só remaneja
                          onRemove={row.editable && !packageSeries
                            ? () => setSeriesRemoved(prev => [...prev, row.item.id])
                            : undefined}
                          onRestore={() => setSeriesRemoved(prev => prev.filter(id => id !== row.item.id))}
                        />
                      ))}
                    </Box>
                    {packageSeries && (
                      <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary, marginTop: '0.4rem' }}>
                        Pacote: as sessões podem mudar de data e horário, mas não sair da série.
                      </Typography>
                    )}
                    {seriesRemoved.length > 0 && (
                      <TextField
                        label="Motivo da remoção"
                        required
                        size="small"
                        fullWidth
                        value={seriesReason}
                        onChange={(e) => setSeriesReason(e.target.value)}
                        error={seriesReasonMissing}
                        helperText={seriesReasonMissing
                          ? `Obrigatório para remover ${seriesRemoved.length === 1 ? 'a sessão' : `as ${seriesRemoved.length} sessões`}.`
                          : undefined}
                        inputProps={{ maxLength: 500 }}
                        sx={{ marginTop: '0.75rem' }}
                      />
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <Button
                        variant="text"
                        size="small"
                        disabled={saving}
                        onClick={cancelSeriesEditing}
                        sx={{ textTransform: 'none', color: colors.textSecondary }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        disableElevation
                        disabled={saving || seriesPending === 0 || seriesInvalid || seriesReasonMissing}
                        onClick={applySeriesEditing}
                        sx={{
                          textTransform: 'none',
                          backgroundColor: colors.primary,
                          '&:hover': { backgroundColor: colors.primaryHover },
                        }}
                      >
                        {seriesPending > 0
                          ? `Aplicar ${seriesPending} ${seriesPending === 1 ? 'alteração' : 'alterações'}`
                          : 'Aplicar'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                <Box sx={{
                  maxHeight: 168,
                  overflowY: 'auto',
                  border: `1px solid ${colors.backgroundAlt}`,
                  borderRadius: '6px',
                  padding: '0.4rem 0.75rem',
                }}>
                  {(seriesItems ?? []).map(item => {
                    const holiday = holidayMap[item.dateTimeInitial.slice(0, 10)];
                    const isCurrent = item.id === currentScheduleId;
                    const alerts = item.active && !item.cancelled ? seriesItemAlerts(item) : [];
                    return (
                      <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                        <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{
                          fontSize: '0.9rem',
                          lineHeight: 1.9,
                          fontWeight: isCurrent ? typography.fontWeight.semibold : undefined,
                          textDecoration: item.active ? undefined : 'line-through',
                          color: !item.active
                            ? colors.textSecondary
                            : item.cancelled
                              ? colors.error
                              : holiday ? '#B26A00' : colors.textPrimary,
                        }}>
                          {formatSlot(item.dateTimeInitial)}
                          {/* Legado do Bubble pode ter final == inicial (duração zero) — aí só o início */}
                          {item.dateTimeFinal > item.dateTimeInitial ? `–${endHourOf(item.dateTimeFinal)}` : ''}
                          {isCurrent ? ' (esta)' : ''}
                          {!item.active ? ' — excluído' : ''}
                          {item.active && holiday ? ` — feriado: ${holiday}` : ''}
                          {/* RN-228: status da ocorrência com a cor do catálogo, visível sem hover */}
                          {item.active && item.statusDisplay && (
                            <Box component="span" sx={{
                              marginLeft: '0.5rem', padding: '1px 8px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 600,
                              color: 'white', backgroundColor: item.statusColor || colors.textSecondary, verticalAlign: 'middle', whiteSpace: 'nowrap',
                            }}>
                              {item.statusDisplay}
                            </Box>
                          )}
                        </Typography>
                        {/* Aviso já na abertura (pedido do Hilton, 30/09): texto visível, sem depender
                            de hover, para servir também no celular */}
                        {alerts.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '0.3rem', color: '#8a5300', fontSize: '0.78rem', lineHeight: 1.35, marginBottom: '0.25rem' }}>
                            <WarningAmber sx={{ fontSize: '0.95rem', marginTop: '1px', flexShrink: 0 }} />
                            <span>{alerts.join(' · ')}</span>
                          </Box>
                        )}
                        </Box>
                        {/* Abre a ocorrência clicada no próprio modal (excluída não abre —
                            a agenda não a carrega); com edição em curso fica desabilitado
                            para não descartar mudanças em silêncio */}
                        {!isCurrent && item.active && onOpenOccurrence && (
                          <Tooltip
                            title={isDirty
                              ? 'Salve ou cancele as alterações para navegar'
                              : 'Abrir este agendamento'}
                            placement="top"
                            enterTouchDelay={300}
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={saving || isDirty}
                                onClick={() => openOccurrenceAndScrollTop(item.id, item.dateTimeInitial)}
                                sx={{ padding: '2px', color: colors.primary }}
                              >
                                <Launch sx={{ fontSize: '0.95rem' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                )}
              </Box>
            )}

            {/* Valores calculados - Visível apenas se showPricing for true */}
            {showPricing && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '1rem' }}>
                <TextField
                  label="Valor Unitário"
                  value={formData.unitValue ? `R$ ${formData.unitValue}` : ''}
                  disabled
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontSize: inputs.default.labelFontSize,
                      color: inputs.default.labelColor,
                      backgroundColor: inputs.default.labelBackground,
                      padding: inputs.default.labelPadding,
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: inputs.default.height,
                      fontSize: inputs.default.fontSize,
                      backgroundColor: colors.backgroundAlt,
                      '& fieldset': {
                        borderColor: inputs.default.borderColor,
                      },
                    },
                  }}
                />

                <TextField
                  label="Desconto Unitário"
                  value={formData.discount ? `R$ ${formData.discount}` : 'R$ 0.00'}
                  disabled
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontSize: inputs.default.labelFontSize,
                      color: inputs.default.labelColor,
                      backgroundColor: inputs.default.labelBackground,
                      padding: inputs.default.labelPadding,
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: inputs.default.height,
                      fontSize: inputs.default.fontSize,
                      backgroundColor: colors.backgroundAlt,
                      '& fieldset': {
                        borderColor: inputs.default.borderColor,
                      },
                    },
                  }}
                />

                <TextField
                  label="Total Unitário"
                  value={formData.totalUnit ? `R$ ${formData.totalUnit}` : ''}
                  disabled
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontSize: inputs.default.labelFontSize,
                      color: inputs.default.labelColor,
                      backgroundColor: inputs.default.labelBackground,
                      padding: inputs.default.labelPadding,
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: inputs.default.height,
                      fontSize: inputs.default.fontSize,
                      backgroundColor: colors.backgroundAlt,
                      '& fieldset': {
                        borderColor: inputs.default.borderColor,
                      },
                    },
                  }}
                />

                {/* Rótulo diz de ONDE vem o número: total gravado/calculado da SÉRIE
                    (ex.: 12×220 = R$ 2.640) lido como valor da sessão confundia — achado
                    do Hilton na edição de uma ocorrência, 21/08 */}
                <TextField
                  label={totalSeriesSize > 1 ? `Total da série (${totalSeriesSize} sessões)` : 'Total'}
                  value={formData.totalValue ? `R$ ${formData.totalValue}` : ''}
                  disabled
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontSize: inputs.default.labelFontSize,
                      color: inputs.default.labelColor,
                      backgroundColor: inputs.default.labelBackground,
                      padding: inputs.default.labelPadding,
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: inputs.default.height,
                      fontSize: inputs.default.fontSize,
                      backgroundColor: colors.backgroundAlt,
                      '& fieldset': {
                        borderColor: inputs.default.borderColor,
                      },
                    },
                    '& input': {
                      fontWeight: typography.fontWeight.semibold,
                    },
                  }}
                />
              </Box>
            )}

          </Box>
        </Box>

        {/* Histórico (edição): trilha de auditoria traduzida — cobre o que passou pelo
            sistema novo; mudanças feitas no legado chegam sem trilha */}
        {isEdit && loadHistory && (
          <Box sx={{ marginTop: '2rem' }}>
            <Typography sx={{
              fontSize: '0.9rem',
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
              marginBottom: '0.5rem',
            }}>
              Histórico
              {rescheduleCount > 0 ? ` — ${rescheduleCount} remarcação(ões)` : ''}
            </Typography>
            <Box sx={{
              maxHeight: 180,
              overflowY: 'auto',
              border: `1px solid ${colors.backgroundAlt}`,
              borderRadius: '6px',
              padding: '0.5rem 0.75rem',
            }}>
              {history === null && (
                <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                  Carregando...
                </Typography>
              )}
              {history !== null && history.length === 0 && (
                <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                  Sem registros de alteração para este agendamento.
                </Typography>
              )}
              {(history ?? []).map((entry, index) => {
                const line = `${formatHistoryDate(entry.occurredAt)} — ${entry.action}`
                  + (entry.user ? ` — ${entry.user}` : '');
                return (
                  // Linha única com reticências; texto completo no tooltip (hover no
                  // desktop, toque longo no mobile)
                  <Tooltip key={index} title={line} placement="top-start" enterTouchDelay={300}>
                    <Typography noWrap sx={{ fontSize: '0.8rem', lineHeight: 1.8 }}>
                      {line}
                    </Typography>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        padding: { xs: '1rem', sm: '1.5rem 2rem' },
        borderTop: `1px solid ${colors.backgroundAlt}`,
        backgroundColor: colors.background,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        {/* Dropdowns de Confirmação e Presença — SÓ NA EDIÇÃO (a criação nasce sempre com
            os defaults "Pendente confirmação"/"Não informado"). Campos largos: os nomes do
            catálogo são longos ("Cancelado profissional") */}
        <Box sx={{ display: 'flex', gap: '1rem', flex: '1 1 auto', flexWrap: 'wrap' }}>
          {isEdit && (<>
          <TextField
            select
            label="Confirmação"
            value={formData.confirmationStatus}
            disabled={locked || statusLocked}
            onChange={(e) => {
              const next = e.target.value;
              // Confirmação finalizadora zera a presença NO FORMULÁRIO (visível ao usuário);
              // o servidor recusa a combinação de qualquer forma (RN-114b)
              const clearPresence = (terminalConfirmations ?? []).includes(next);
              setFormData({
                ...formData,
                confirmationStatus: next,
                attendanceStatus: clearPresence ? 'Não informado' : formData.attendanceStatus,
              });
            }}
            SelectProps={{ MenuProps: selectMenuProps }}
            InputLabelProps={{
              shrink: true,
              sx: {
                fontSize: inputs.select.labelFontSize,
                color: inputs.select.labelColor,
                backgroundColor: inputs.select.labelBackground,
                padding: inputs.select.labelPadding,
              },
            }}
            sx={{
              minWidth: { xs: '100%', sm: '220px' },
              flex: '1 1 220px',
              maxWidth: '280px',
              '& .MuiOutlinedInput-root': {
                height: inputs.default.height,
                fontSize: inputs.default.fontSize,
                '& fieldset': {
                  borderColor: inputs.select.borderColor,
                },
              },
            }}
          >
            {/* Sem opção vazia: o registro sempre nasce com status (fallback só para
                legado sem valor gravado) */}
            {!formData.confirmationStatus && <MenuItem value="" disabled>—</MenuItem>}
            {withCurrent(
              confirmationOptions ?? ['Pendente confirmação', 'Confirmado'],
              formData.confirmationStatus || undefined,
            ).map(name => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </TextField>

          <Tooltip
            title={(terminalConfirmations ?? []).includes(formData.confirmationStatus)
              ? 'Status finalizador: agendamento cancelado não tem presença. Cancelamento em cima da hora que cobra usa a presença "Não compareceu - Não avisou", sem status de cancelamento.'
              : presenceTooEarly ? 'Disponível a partir do dia do atendimento' : ''}
            placement="top"
            enterTouchDelay={300}
          >
          <TextField
            select
            label="Presença"
            value={formData.attendanceStatus}
            disabled={locked || statusLocked || presenceTooEarly
              || (terminalConfirmations ?? []).includes(formData.confirmationStatus)}
            onChange={(e) => setFormData({ ...formData, attendanceStatus: e.target.value })}
            SelectProps={{ MenuProps: selectMenuProps }}
            InputLabelProps={{
              shrink: true,
              sx: {
                fontSize: inputs.select.labelFontSize,
                color: inputs.select.labelColor,
                backgroundColor: inputs.select.labelBackground,
                padding: inputs.select.labelPadding,
              },
            }}
            sx={{
              minWidth: { xs: '100%', sm: '220px' },
              flex: '1 1 220px',
              maxWidth: '280px',
              '& .MuiOutlinedInput-root': {
                height: inputs.default.height,
                fontSize: inputs.default.fontSize,
                '& fieldset': {
                  borderColor: inputs.select.borderColor,
                },
              },
            }}
          >
            {!formData.attendanceStatus && <MenuItem value="" disabled>—</MenuItem>}
            {withCurrent(
              presenceOptions ?? ['Não informado', 'Compareceu'],
              formData.attendanceStatus || undefined,
            ).map(name => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </TextField>
          </Tooltip>
          {/* Motivo do cancelamento: obrigatório ao MUDAR para "Cancelado *"; exibido
              (somente leitura pelo disabled da janela) quando já cancelado */}
          {isCancelling && (
            <TextField
              label="Motivo do cancelamento"
              value={formData.cancellationReason ?? ''}
              onChange={(e) => setFormData({ ...formData, cancellationReason: e.target.value })}
              disabled={locked || statusLocked}
              required
              fullWidth
              inputProps={{ maxLength: 500 }}
              placeholder="Obrigatório ao cancelar"
              InputLabelProps={{ shrink: true }}
              sx={{
                flexBasis: '100%',
                '& .MuiOutlinedInput-root': {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                },
              }}
            />
          )}
          </>)}
        </Box>

        {/* Botões de ação. Os avisos (pago/diferença/bloqueio) ficam num bloco que ENCOLHE e
            quebra linha; sem isso, texto longo empurrava os botões para fora do modal. */}
        <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', flex: '1 1 auto', minWidth: 0 }}>
          {(paid || paymentDiff !== null || (mode === 'edit' && !locked && deleteBlockedByStatus)) && (
          <Box sx={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {paid && (
            <Typography sx={{ fontSize: '0.8rem', color: colors.error, fontWeight: typography.fontWeight.medium }}>
              {paymentDate
                ? `Pago em ${formatPaymentDate(paymentDate)} — não pode ser excluído`
                : 'Pago — não pode ser excluído'}
            </Typography>
          )}
          {paymentDiff !== null && (
            <Typography sx={{ fontSize: '0.8rem', color: '#B26A00', fontWeight: typography.fontWeight.medium }}>
              {`Pago R$ ${paymentFinalValue!.toFixed(2)} × valor R$ ${Number(formData.totalUnit).toFixed(2)}${paymentDiscount ? ` (desconto de pagamento R$ ${paymentDiscount.toFixed(2)})` : ''} — `}
              {paymentDiff > 0
                ? `diferença de R$ ${paymentDiff.toFixed(2)} a cobrar`
                : `R$ ${Math.abs(paymentDiff).toFixed(2)} pago a maior`}
            </Typography>
          )}
          {mode === 'edit' && !paid && !locked && deleteBlockedByStatus && (
            <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, fontWeight: typography.fontWeight.medium }}>
              Com presença ou confirmação marcada — não pode ser excluído
            </Typography>
          )}
          </Box>
          )}
          {/* No celular os botões dividem a linha inteira: com flexShrink 0 e padding de
              1.5rem cada, os três estouravam a largura e vazavam pelas bordas do modal
              (achado do Hilton, 02/09). */}
          <Box sx={{
            display: 'flex',
            gap: { xs: '0.5rem', sm: '1rem' },
            flexShrink: 0,
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}>
          {mode === 'edit' && !paid && !locked && (
            <Button
              onClick={() => {
                if (!onDelete) {
                  return;
                }
                setDeleteScope(packageSeries ? 'following' : 'single');
                setDeleteDialogOpen(true);
              }}
              disabled={saving || deleteBlockedByStatus || packageDeleteBlocked || !onDelete || !canDelete}
              title={!canDelete
                ? 'Seu perfil não exclui agendamentos'
                : packageStarted
                ? 'Pacote já iniciado (presença marcada): não é possível excluir sessões. Remaneje as que faltam.'
                : packageDeleteBlocked
                  ? 'O pacote tem sessões pagas ou com status marcado: não pode ser excluído. Remaneje as sessões que faltam.'
                  : undefined}
              variant="contained"
              sx={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                borderRadius: '6px',
                backgroundColor: colors.error,
                color: colors.white,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#c82333',
                  boxShadow: 'none',
                  transform: 'translateY(-1px)',
                }
              }}
            >
              Excluir
            </Button>
          )}

          {/* "Agendar em seguida" (item 145): grava este e abre outro encostado nele — mesmo
              paciente, profissional e dia, comecando onde este termina. So na CRIACAO: em edicao
              nao existe "proximo". Contained-outline para nao competir com o Salvar, que e a acao
              principal. */}
          {mode === 'create' && onScheduleNext && (
            <Button
              onClick={() => handleSave(true)}
              variant="outlined"
              disabled={endTimeInvalid || saving || locked || !isDirty || requiredMissing || cancellationReasonMissing || creationSlotsInvalid || seriesEditing}
              sx={{
                padding: '0.75rem 1.25rem',
                fontSize: '1rem',
                borderRadius: '6px',
                borderColor: colors.primary,
                color: colors.primary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                // No celular o rótulo longo não cabe ao lado do Agendar: desce para linha própria, abaixo da ação principal
                flex: { xs: '1 1 100%', sm: '0 0 auto' },
                order: { xs: 2, sm: 0 },
              }}
            >
              Salvar e agendar em seguida
            </Button>
          )}

          <Button
            onClick={() => handleSave(false)}
            variant="contained"
            disabled={endTimeInvalid || saving || locked || !isDirty || requiredMissing || cancellationReasonMissing || creationSlotsInvalid || seriesEditing}
            sx={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              borderRadius: '6px',
              flex: { xs: '1 1 auto', sm: '0 0 auto' },
              backgroundColor: colors.primary,
              color: colors.white,
              fontWeight: typography.fontWeight.medium,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#029AAB',
                boxShadow: 'none',
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                backgroundColor: '#a8dde3',
                color: colors.white,
              }
            }}
          >
            {mode === 'create' ? 'Agendar' : 'Salvar'}
          </Button>
          </Box>
        </Box>
      </DialogActions>

      {/* Diálogo de mover (edição de série com mudança de data/horário) */}
      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1.05rem', fontWeight: typography.fontWeight.semibold }}>
          Este agendamento faz parte de uma série
        </DialogTitle>
        <DialogContent>
          <RadioGroup
            value={moveScope}
            onChange={(e) => setMoveScope(e.target.value as 'single' | 'series')}
          >
            <FormControlLabel
              value="single"
              control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
              label="Somente este agendamento"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
            />
            <FormControlLabel
              value="series"
              control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
              label="Este e os seguintes da série"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
            />
          </RadioGroup>
          {moveScope === 'series' && (
            <Box sx={{ marginTop: '0.75rem', maxHeight: 220, overflowY: 'auto' }}>
              <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, marginBottom: '0.4rem' }}>
                Ocorrências que serão alteradas:
              </Typography>
              {(currentSeriesItem ? [currentSeriesItem, ...followingItems] : followingItems).map(item => (
                <Typography key={item.id} sx={{ fontSize: '0.82rem', lineHeight: 1.7 }}>
                  {dateTimeChanged
                    ? `${formatSlot(item.dateTimeInitial)} → ${formatMovedSlot(item.dateTimeInitial)}`
                    : formatSlot(item.dateTimeInitial)}
                  {item.id === currentScheduleId ? ' (este)' : ''}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmMove}
            sx={{ textTransform: 'none', backgroundColor: colors.primary, boxShadow: 'none' }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de exclusão: motivo obrigatório; série pode incluir "este em diante" */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1.05rem', fontWeight: typography.fontWeight.semibold }}>
          Excluir agendamento
        </DialogTitle>
        <DialogContent>
          {packageSeries && (
            <Box sx={{ marginBottom: '0.75rem', maxHeight: 180, overflowY: 'auto' }}>
              <Typography sx={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                Este serviço é um pacote: o pacote inteiro será excluído ({activeSeriesItems.length} sessões).
              </Typography>
              {activeSeriesItems.map(item => (
                <Typography key={item.id} sx={{ fontSize: '0.82rem', lineHeight: 1.7 }}>
                  {formatSlot(item.dateTimeInitial)}
                </Typography>
              ))}
            </Box>
          )}
          {!packageSeries && followingItems.length > 0 && (
            <RadioGroup
              value={deleteScope}
              onChange={(e) => setDeleteScope(e.target.value as 'single' | 'following')}
              sx={{ marginBottom: '0.5rem' }}
            >
              <FormControlLabel
                value="single"
                control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                label="Somente este agendamento"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
              />
              <FormControlLabel
                value="following"
                control={<Radio sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />}
                label="Este e os seguintes da série"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
              />
            </RadioGroup>
          )}
          {!packageSeries && deleteScope === 'following' && (
            <Box sx={{ marginBottom: '0.75rem', maxHeight: 180, overflowY: 'auto' }}>
              <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, marginBottom: '0.4rem' }}>
                Serão excluídos:
              </Typography>
              {currentSeriesItem && (
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7 }}>
                  {formatSlot(currentSeriesItem.dateTimeInitial)} (este)
                </Typography>
              )}
              {followingItems.map(item => {
                const deletable = item.pristine && !item.paid;
                // Motivo específico da preservação: pagamento ou status fora do default
                const reason = item.paid ? 'pago' : 'com status marcado';
                return (
                  <Typography key={item.id} sx={{
                    fontSize: '0.82rem',
                    color: deletable ? colors.textPrimary : colors.textSecondary,
                    lineHeight: 1.7,
                  }}>
                    {formatSlot(item.dateTimeInitial)}
                    {deletable ? '' : ` — mantido (${reason})`}
                  </Typography>
                );
              })}
            </Box>
          )}
          <TextField
            label="Motivo da exclusão"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            required
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 500 }}
            placeholder="Obrigatório"
            InputLabelProps={{ shrink: true }}
            sx={{ marginTop: '0.25rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteReason.trim().length === 0}
            sx={{ textTransform: 'none', backgroundColor: colors.error, boxShadow: 'none', '&:hover': { backgroundColor: '#c82333' } }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AppointmentModal;
